/* GASSISA
=========================================================================================
* Descripción: 
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 23/11/2023 | Lic. José Luis Aragón Cervantes  | Creación
 *      2       | 04/06/2026 | Lic. José Luis Aragón Cervantes  | Orden configurable de menús
 *      3       | 04/06/2026 | Lic. José Luis Aragón Cervantes  | Orden estable en menú de sesión
 *      4       | 26/08/2026 | Lic. José Luis Aragón Cervantes  | Orden configurable de grupos de menú
*      5       | 27/08/2026 | Lic. José Luis Aragón Cervantes  | Lectura estándar de menús por Orden y OrdenGrupo
 *      6       | 27/08/2026 | Lic. José Luis Aragón Cervantes  | Validación y cierre de sesión cada 30 minutos
 *      7       | 31/08/2026 | Lic. José Luis Aragón Cervantes  | Refresco fallido cierra sesión solo en arranque
 *      8       | 22/09/2026 | Lic. José Luis Aragón Cervantes  | Sesión centralizada: sessionStorage, refreshSession único
 *                 |            |                                  single-flight, preventivo 15min, logout con guard
 *      9       | 23/09/2026 | LIC. José Luis Aragón C.                       | Login usa JWE fresco para el menú
 *                 |            |                                  | (el login rota el tenant JWE)
  *      10      | 23/09/2026 | LIC. José Luis Aragón C.                       | Revertido pre-refresh de JWE en
 *                 |            |                                  | login: rompía el gate sin PrinterTools
  *      11      | 24/09/2026 | LIC. José Luis Aragón C.                       | Revertido handleLogoutTimer roto
  *                 |            |                                  | (cerraba sesión en cada actividad);
  *                 |            |                                  | logout con recarga completa a /login
  *                 |            |                                  | para renovar JWE como primera entrada
  *      12      | 08/10/2026 | LIC. José Luis Aragón C.                       | Inactividad cross-tab: heartbeat
  *                 |            |                                  | compartido + evento storage (una
  *                 |            |                                  | pestaña idle ya no tumba la sesión
  *                 |            |                                  | de la pestaña activa); eventos
  *                 |            |                                  | keydown/touchstart/wheel
  *  ---------------------------------------------------------------------------------------
  */

import { createContext, useContext, useState, useEffect, useRef } from "react";

function fromEntries(entries) {
  const obj = {};
  for (const [key, val] of entries) {
    obj[key] = val;
  }
  return obj;
}
import {
  apiLogin,
  apiLoginRefresh,
  apiCambiarPasswordInicial,
  apiPostMenuModelUser,
  apiParametrosCaja,
  apiParametrosGet,
  apiCompuValidator,
  serverSeguridad,
  apiTenantDeviceValidate,
  apiTenantMobileDeviceValidate,
  apiTenantDeviceFindByName,
  apiTenantDeviceReassign,
  apiTenantDeviceDatabaseName,
  apiTenantDeviceListByHardware,
  apiTenantActivationRedeem,
  apiTenantMobileActivationRedeem,
  apiTenantMobileChallengeSend,
  apiTenantMobileChallengeConfirm,
  apiTenantMobileDeviceRecover,
  apiTenantMobileDeviceAccessRequest,
  apiTenantProvisioningRun,
  apiTenantAdminLogin,
  apiTenantPrinterToolsDownload,
  apiTenantLocalDeviceLicense,
  apiTenantLocalDeviceValidate,
  apiTenantLocalFingerprint,
  apiTenantMfaSetup,
  apiTenantMfaConfirm
} from "../../settings/ApiConfig";
import { isErrorForm, isNullOrEmpty, tiempoCierreSession, newId } from "../../settings/utils";
import {
  tokenName,
  tokenRefreshName,
  tenantAccessTokenName,
  getSession,
  saveSession,
  clearUserSession,
  getAuthorizationToken,
} from "../../settings/sessionStorage";
import { registerRequestErrorLog } from "../../settings/errorLogStorage";
import { registerSessionHandlers } from "../../settings/sessionRefreshBridge";
import { defaultTypes } from "../../settings/enums";
import { routeLogin, routeRoot, routeAcercaDe, routeSistem, routeTenantAdminPasswordRecovery } from "../../settings/routeConfig";
import { useLocation, useNavigate } from "react-router-dom";
import { useLayout } from "./LayoutContext";

import { useRequest } from "./RequestContext";

import { rxCaracteresEsp } from "../../settings/regexConfig";
import {
  listTenantProfiles as listStoredTenantProfiles,
  saveTenantProfile as persistTenantProfile,
  findTenantProfile as findStoredTenantProfile,
} from "../../settings/tenantProfilesStorage";

// Las claves físicas de sesión viven en settings/sessionStorage.js; aquí se reexportan
// para conservar compatibilidad con consumidores existentes.
export { tokenName, tokenRefreshName, tenantAccessTokenName };
export const arsyncTenantDeviceName = "arsync-tenant-device";
export const arsyncTenantActivationName = "arsync-tenant-activation";
export const tenantActivationGuidPattern = /^ARXS-[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}$/;
const tenantDebugLogName = "arsync-tenant-debug-log";
const tenantDebugLogEnabled = false; // Activar temporalmente solo para diagnostico de aprobacion movil.

export const tenantGateStatuses = {
  BOOTSTRAP: "BOOTSTRAP",
  NEEDS_ACTIVATION: "NEEDS_ACTIVATION",
  VALIDATING: "VALIDATING",
  AUTHORIZED: "AUTHORIZED",
  DENIED: "DENIED",
  PROVISIONING: "PROVISIONING",
  PRINTERTOOLS_REQUIRED: "PRINTERTOOLS_REQUIRED",
  PENDING: "PENDING",
  LOOKUP_BY_NAME: "LOOKUP_BY_NAME",
  MFA_REQUIRED: "MFA_REQUIRED",
  MFA_CONFIRMED: "MFA_CONFIRMED",
  ADMIN_DASHBOARD: "ADMIN_DASHBOARD",
  DATABASE_NOT_FOUND: "DATABASE_NOT_FOUND",
  NEEDS_REASSIGNMENT: "NEEDS_REASSIGNMENT",
  PENDING_APPROVAL: "PENDING_APPROVAL",
};

const tenantRequestTimeoutMs = 8000;

// Política única de sesión (backend: SessionOptions Inactivity=30/Access=30/Refresh=7d).
// tiempoCierreSession (30min) es la expresión en frontend de InactivityMinutes y de
// AccessTokenMinutes; el refresh preventivo corre a la mitad (15min) para renovar JWT/JWE
// antes de su expiración técnica. No crear otras constantes de tiempo de sesión.
const preventiveRefreshMs = Math.floor(tiempoCierreSession / 2);

const isMobileTenantDevice = () => {
  const mobileAgent = /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || "");
  const coarsePointer = window.matchMedia?.("(pointer: coarse)")?.matches === true;
  return mobileAgent || (coarsePointer && Math.min(window.screen.width, window.screen.height) < 900);
};

const getLoginForm = () => ({
  username: { value: "", error: false },
  password: { value: "", error: false },
});

const getConfiguracionDefault = () => ({
  tituloNavegador: "SIZESOFTX",
  tituloDependencia: "PLATAFORMA DE SIZESOFTX",
  metaDescription: "Plataforma de sizesoftx",
  colorPrimario: "#095fb4", // #FD8204
  colorSecundario: "#0A5DA6", // #16216A
  contrastePrimario: "#FFFFFF",
  contrasteSecundario: "#FFFFFF",
  urlFuente: "https://fonts.googleapis.com/css?family=Roboto:300,400,500,700&display=swap",
  nombreFuente: "Roboto",
  rutaImagenFondo: "",
  rutaImagenLogo: "",
  rutaImagenPortal: "",
});

const getCambiarPasswordForm = () => ({
  username: { value: "", error: false },
});

const getTenantActivationForm = () => ({
  licenseGuid: { value: "", error: false },
  companyName: { value: "", error: false },
  businessName: { value: "", error: false },
  adminName: { value: "", error: false },
  adminEmail: { value: "", error: false },
  adminPhone: { value: "", error: false },
  deviceAlias: { value: "", error: false },
  adminPassword: { value: "", error: false },
  adminPasswordConfirm: { value: "", error: false },
  mfaCode: { value: "", error: false },
  claveActivacion: { value: "", error: false },
});

const normalizeTenantActivationForm = (form) => ({
  ...getTenantActivationForm(),
  ...(form ?? {}),
});

const formatTenantActivationGuid = (value) => {
  let cleanValue = (value ?? "").replace(/[^a-z0-9]/gi, "").toUpperCase();
  if (cleanValue.startsWith("ARXS")) {
    cleanValue = cleanValue.slice(4);
  }

  cleanValue = cleanValue.slice(0, 12);
  const sections = [4, 4, 4];
  let cursor = 0;

  const licenseBody = sections
    .map((sectionLength) => {
      const section = cleanValue.slice(cursor, cursor + sectionLength);
      cursor += sectionLength;
      return section;
    })
    .filter(Boolean)
    .join("-");

  return licenseBody ? `ARXS-${licenseBody}` : "ARXS-";
};

const getDefinicionMenu = () => ({
  Administracion: {
    nombre: "Administración",
    mostrar: false,
    tiposuario: "USUARIO",
    _paginas: {
      nombre: "",
      path: "",
      mostrar: false,
      tipousuario: "USUARIO",
      hotkey: "",
    },
  },
  Seguridad: {
    nombre: "Seguridad",
    mostrar: false,
    tipusuario: "",
    _paginas: {
      nombre: "",
      path: "",
      mostrar: false,
      tipusuario: "",
      hotkey: "",
    },
  },
  _paths: [],
});

const getPermisosHerramientas = () => ({
  IDUser: "",
  MenuModel: "",
  Status: false,
  Path: ""
});

const EquipoData = () => ({
  namepc: { value: "", error: false },
  idpc: { value: "", error: false },
  locality: { value: "", error: false }
});

const getParametrosCajaForm = () => ({
  //Data
  idcaja: defaultTypes.Guid,
  fecharegistro: defaultTypes.Date,
  caja: "CAJA-DEFAULT",
  dominio: "ALMACEN1",
  tipoventa: "VENTA",
  //Folios
  foliocortecaja: defaultTypes.int,
  folioventa: defaultTypes.int,
  foliopreventa: defaultTypes.int,
  foliocotizacion: defaultTypes.int,
  foliodevolucion: defaultTypes.int,
  folioapartado: defaultTypes.int,

  //Encabezado y pie de ticket
  encabezadoticket: defaultTypes.string,
  pieticket: defaultTypes.string,

  //Configuracion adicional
  diasalertacaducidad: defaultTypes.int,
  statusaperturacajon: defaultTypes.string,
  chr1: defaultTypes.string,
  parametrop: defaultTypes.string,
  chr2: defaultTypes.string,
  chr3: defaultTypes.string,
  chr4: defaultTypes.string,

  statusspeech: defaultTypes.int,
  reconocimientovoz: defaultTypes.int,

  horaactualizaciondiaria: defaultTypes.string,
  statusviewclient: defaultTypes.string,
  locationviewclient: defaultTypes.string,
  sizeviewclient: defaultTypes.string,
  diasalertacuentaxpagar: defaultTypes.int,

  //Status impresoras
  statusimpresoraventas: defaultTypes.int,
  statusimpresorapreventas: defaultTypes.int,
  statusimpresoracotizaciones: defaultTypes.int,
  statusimpresoraapartados: defaultTypes.int,
  statusimpresorareportes: defaultTypes.int,
  statusimpresorabanner: defaultTypes.int,
  statusimpresoraetiqueta: defaultTypes.int,
  statusimpresoracodigobarras: defaultTypes.int,

  //Nombres impresoras
  nombreimpresoraventas: defaultTypes.string,
  nombreimpresorapreventas: defaultTypes.string,
  nombreimpresoracotizaciones: defaultTypes.string,
  nombreimpresoraapartados: defaultTypes.string,
  nombreimpresorareportes: defaultTypes.string,
  nombreimpresorabanner: defaultTypes.string,
  nombreimpresoraetiqueta: defaultTypes.string,
  nombreimpresoracodigobarras: defaultTypes.string,

  //WhatsApp
  idphonewhatsapp: defaultTypes.string,
  tokenaccesowhatsapp: defaultTypes.string,

  // El diseño personalizado sólo se habilita cuando existe en Parametros.
  ticketLayout: {},
  ticketLayoutRegistrado: false,
});

export const SesionContext = createContext();
export const SesionProvider = (props) => {
  const { handleOpenAlert, handleOpenLoader, handleCloseLoader } = useLayout();

  const { GetRequest, GetFileRequest, PostRequest, token, setToken } = useRequest();

  const [parametrosCajaForm, setParametrosCajaForm] = useState(getParametrosCajaForm());

  const location = useLocation();
  const navigate = useNavigate();

  const [usuario, setUsuario] = useState(null);
  const [usuarioData, setUsuarioData] = useState([]);
  const [tokenSeguridad, setTokenSeguridad] = useState("eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiJzaXN0ZW1hU2l6ZVNvZnRYIiwianRpIjoiMjEzMDhkYjYtYmE2Yy00MTU5LTgyZmQtOWZjNzAwMGEwMTliIiwicm9sZSI6ImFkbWluIiwiZXhwIjoxNzMxODIwMzUzLCJpc3MiOiJodHRwczovLyo6NTAwMSIsImF1ZCI6Imh0dHBzOi8vKjozMDAwIn0.kJTI3zDSkSxPcS1YWT1J48mVX_8iGiNVHJd6e7C9gCs");
  const [tokenRefresh, setTokenRefresh] = useState("");
  const [permisosModulos, setPermisosModulos] = useState([]);
  const [configuracion, setConfiguracion] = useState(getConfiguracionDefault());

  const [usuarioEnSesion, setUsuarioEnSesion] = useState(false);
  const [tipoUsuario, setTipoUsuario] = useState("USUARIO");
  const [loginForm, setLoginForm] = useState(getLoginForm());
  const [tenantActivationForm, setTenantActivationForm] = useState(getTenantActivationForm());
  const [tenantGateStatus, setTenantGateStatus] = useState(tenantGateStatuses.BOOTSTRAP);
  const [tenantGateMessage, setTenantGateMessage] = useState("");
  const [tenantCorrelationId, setTenantCorrelationId] = useState("");
  const [tenantMfaSetup, setTenantMfaSetup] = useState(null);
  const [isRedeemingTenant, setIsRedeemingTenant] = useState(false);
  const [isMobileTenant] = useState(() => isMobileTenantDevice());
  const [isRecoveringMobileTenant, setIsRecoveringMobileTenant] = useState(false);
  const [mobileVerificationChallenge, setMobileVerificationChallenge] = useState(null);
  const [tenantAdminStatus, setTenantAdminStatus] = useState(null);
  const [tenantAdminDevice, setTenantAdminDevice] = useState(null);
  const [previousRoute, setPreviousRoute] = useState("");
  // Rev.12 LIC. José Luis Aragón C. | Multi-tenant aditivo: lista de licencias/BDs vinculadas a este equipo.
  // No sustituye arsync-tenant-device (perfil activo legacy) ni PrinterTools single-slot.
  const [tenantProfiles, setTenantProfiles] = useState(() => {
    try {
      return listStoredTenantProfiles();
    } catch {
      return [];
    }
  });
  const [cambiarPasswordForm, setCambiarPasswordForm] = useState(getCambiarPasswordForm());
  const [listPermisosObjects, setListPermisosObjects] = useState([getPermisosHerramientas()]);

  const [definicionMenu, setDefinicionMenu] = useState(getDefinicionMenu());

  const [modalCambioPassword, setModalOpenCambioPasswordForm] = useState(false);

  const [pcData, setPcData] = useState(EquipoData());
  const [tenantDebugLog, setTenantDebugLog] = useState(() => {
    if (!tenantDebugLogEnabled) {
      try {
        localStorage.removeItem(tenantDebugLogName);
      } catch {}
      return "";
    }

    return localStorage.getItem(tenantDebugLogName) || "";
  });

  const bootstrapPromiseRef = useRef(null);

  // Actividad de usuario: resetea el timer de inactividad. Incluye teclado
  // (keydown cubre atajos y lector; keypress solo imprimibles), tactil
  // (touchstart para portatiles/tabletas) y wheel (tablas con scroll interno
  // no disparan scroll de window).
  const events = ["load", "mousemove", "mousedown", "click", "scroll", "keypress", "keydown", "touchstart", "wheel"];
  // Heartbeat cross-tab: la inactividad se mide por NAVEGADOR, no por pestaña.
  // Sin esto, una pestaña idle 30min hace logout y borra los tokens compartidos
  // (clearUserSession), y la pestaña donde se trabaja cae a login en su
  // siguiente 401 (retry-refresh sin tokens). Cada actividad reporta timestamp;
  // las demás pestañas rearmam su timer vía evento storage.
  const crossTabActivityKey = "sizesoftx-dashboard-lastactivity";
  const crossTabWriteMs = 10000;
  const lastCrossTabWriteRef = useRef(0);
  const timerRef = useRef(null);
  const refreshTimerRef = useRef(null);
  // Single-flight del refresh: persiste entre renders; concurrentes comparten la promesa.
  const refreshPromiseRef = useRef(null);
  // Evita múltiples logout simultáneos (inactividad + refresh fallido + 401).
  const isLoggingOutRef = useRef(false);

  const createSecureId = () => {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    return newId();
  };

  const createDeviceSecret = () => {
    const bytes = new Uint8Array(32);
    window.crypto?.getRandomValues(bytes);
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  };

  const fetchTenantWithTimeout = async (url, options = {}, timeoutMs = tenantRequestTimeoutMs) => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), timeoutMs);

    try {
      return await fetch(url, {
        ...options,
        signal: controller.signal,
      });
    } finally {
      clearTimeout(timeout);
    }
  };

  const parseTenantDebugJson = (value) => {
    if (isNullOrEmpty(value)) return null;
    try {
      return JSON.parse(value);
    } catch (error) {
      return {
        rawLength: String(value).length,
        parseError: error?.message ?? "No fue posible leer JSON",
      };
    }
  };

  const sanitizeTenantDebugData = (value, depth = 0) => {
    if (value === null || value === undefined) return value;
    if (depth > 8) return "[MAX_DEPTH]";

    if (Array.isArray(value)) {
      return value.map((item) => sanitizeTenantDebugData(item, depth + 1));
    }

    if (typeof value === "object") {
      return Object.entries(value).reduce((acc, [key, item]) => {
        const normalizedKey = key.toLowerCase();
        if (normalizedKey === "responsetext") {
          const stringValue = item === null || item === undefined ? "" : String(item);
          acc[key] = {
            length: stringValue.length,
            preview: stringValue.slice(0, 800).replace(/eyJ[a-zA-Z0-9_\-.]+/g, "[TOKEN_PROTEGIDO]"),
          };
          return acc;
        }
        if (
          normalizedKey.includes("token") ||
          normalizedKey.includes("jwe") ||
          normalizedKey.includes("authorization") ||
          normalizedKey.includes("password") ||
          normalizedKey.includes("secret") ||
          normalizedKey.includes("devicekey") ||
          normalizedKey.includes("tenantaccesstoken")
        ) {
          const stringValue = item === null || item === undefined ? "" : String(item);
          acc[key] = stringValue ? `[PROTEGIDO length=${stringValue.length}]` : "[PROTEGIDO]";
          return acc;
        }

        acc[key] = sanitizeTenantDebugData(item, depth + 1);
        return acc;
      }, {});
    }

    return value;
  };

  const getTenantDebugStorageSnapshot = () => {
    try {
      const tenantDeviceRaw = localStorage.getItem(arsyncTenantDeviceName);
      const tenantActivationRaw = localStorage.getItem(arsyncTenantActivationName);
      const tenantAccessTokenRaw = localStorage.getItem(tenantAccessTokenName);
      const storageKeys = [];
      for (let index = 0; index < localStorage.length; index += 1) {
        const key = localStorage.key(index);
        if (key && /tenant|arsync|token/i.test(key)) storageKeys.push(key);
      }

      return sanitizeTenantDebugData({
        href: window.location.href,
        pathname: window.location.pathname,
        userAgent: navigator.userAgent || "",
        platform: navigator.platform || "",
        language: navigator.language || "",
        cookieEnabled: navigator.cookieEnabled,
        online: navigator.onLine,
        screen: {
          width: window.screen?.width,
          height: window.screen?.height,
          innerWidth: window.innerWidth,
          innerHeight: window.innerHeight,
          devicePixelRatio: window.devicePixelRatio,
        },
        isMobileTenant,
        tenantGateStatus,
        tenantGateMessage,
        storageKeys,
        tenantDevice: parseTenantDebugJson(tenantDeviceRaw),
        tenantActivation: parseTenantDebugJson(tenantActivationRaw),
        tenantAccessToken: tenantAccessTokenRaw ? { exists: true, length: tenantAccessTokenRaw.length } : { exists: false },
      });
    } catch (error) {
      return {
        errorName: error?.name,
        errorMessage: error?.message,
      };
    }
  };

  const appendTenantDebugLog = (eventName, data = {}) => {
    if (!tenantDebugLogEnabled) return;

    const entry = sanitizeTenantDebugData({
      timestamp: new Date().toISOString(),
      eventName,
      data,
    });

    setTenantDebugLog((previousLog) => {
      const serializedEntry = JSON.stringify(entry, null, 2);
      const nextLog = `${previousLog ? `${previousLog}\n\n` : ""}${serializedEntry}`.slice(-90000);
      try {
        localStorage.setItem(tenantDebugLogName, nextLog);
      } catch {}
      return nextLog;
    });
  };

  const clearTenantDebugLog = () => {
    setTenantDebugLog("");
    try {
      localStorage.removeItem(tenantDebugLogName);
    } catch {}
  };

  const copyTenantDebugLog = async () => {
    const textToCopy = tenantDebugLog || "Sin eventos registrados todavia.";
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(textToCopy);
        return;
      }

      const textArea = document.createElement("textarea");
      textArea.value = textToCopy;
      textArea.setAttribute("readonly", "readonly");
      textArea.style.position = "fixed";
      textArea.style.top = "-1000px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
    } catch {}
  };

  const readTenantDebugResponse = async (responseApi) => {
    const responseText = await responseApi.text().catch((error) => `__READ_RESPONSE_ERROR__: ${error?.message ?? "sin detalle"}`);
    let payload = null;
    let parseError = "";

    if (!isNullOrEmpty(responseText) && !responseText.startsWith("__READ_RESPONSE_ERROR__")) {
      try {
        payload = JSON.parse(responseText);
      } catch (error) {
        parseError = error?.message ?? "No fue posible parsear JSON.";
      }
    }

    return { responseText, payload, parseError };
  };

  const validatePrinterToolsLocal = async () => {
    try {
      const response = await fetchTenantWithTimeout(apiTenantLocalFingerprint, {
        method: "GET",
        headers: {
          "Accept": "application/json",
        },
      }, 4000);

      return response.ok;
    } catch {
      return false;
    }
  };

  const getPrinterToolsFingerprint = async () => {
    const response = await fetchTenantWithTimeout(apiTenantLocalFingerprint, {
      method: "GET",
      headers: {
        "Accept": "application/json",
      },
    }, 4000);

    if (!response.ok) {
      return null;
    }

    const payload = await response.json();
    const fingerprint = payload?.fingerprint?.trim();
    if (isNullOrEmpty(fingerprint)) {
      return null;
    }

    return {
      fingerprint,
      machineName: payload?.machineName?.trim() || window.location.hostname,
    };
  };

  const getMachineNameFromPrinterTools = async () => {
    try {
      const response = await fetchTenantWithTimeout(apiTenantLocalFingerprint, {
        method: "GET",
        headers: {
          "Accept": "application/json",
        },
      }, 4000);

      if (!response.ok) return null;
      const payload = await response.json();
      return payload?.machineName?.trim() || null;
    } catch {
      return null;
    }
  };

  const getBrowserStoredTenantDevice = () => {
    try {
      const raw = localStorage.getItem(arsyncTenantDeviceName);
      if (isNullOrEmpty(raw)) return null;
      const parsed = JSON.parse(raw);
      if (isNullOrEmpty(parsed?.tenantKey) || isNullOrEmpty(parsed?.devicePublicId) || isNullOrEmpty(parsed?.deviceKey)) {
        return null;
      }
      return normalizeDeviceData(parsed);
    } catch {
      return null;
    }
  };

  const getStoredTenantDevice = async () => {
    const browserDevice = getBrowserStoredTenantDevice();

    if (isMobileTenant) return browserDevice;

    try {
      const response = await fetchTenantWithTimeout(apiTenantLocalDeviceLicense, {
        method: "GET",
        headers: {
          "Accept": "application/json",
        },
      }, 4000);
      if (response.ok) {
        const payload = await response.json();
        if (payload?.exists && payload?.license?.tenantKey && payload?.license?.devicePublicId) {
          const mergedLicense = {
            ...browserDevice,
            ...payload.license,
            deviceKey: payload.license.deviceKey || browserDevice?.deviceKey || "",
          };

          if (isNullOrEmpty(mergedLicense.deviceKey)) {
            return browserDevice;
          }

          const printerToolsDevice = normalizeDeviceData({
            ...mergedLicense,
            fromPrinterTools: true,
          });
          localStorage.setItem(arsyncTenantDeviceName, JSON.stringify(printerToolsDevice));
          return printerToolsDevice;
        }
      }
    } catch {
    }

    return browserDevice;
  };

  const findTenantDeviceByName = async (nombreEquipo, printerToolsData = null) => {
    if (isNullOrEmpty(nombreEquipo)) {
      return null;
    }
    const normalizedName = nombreEquipo.trim();
    try {
      const response = await fetch(apiTenantDeviceFindByName, {
        method: "POST",
        body: JSON.stringify({
          nombreEquipo: normalizedName,
          fingerprint: String(printerToolsData?.fingerprint ?? "").trim(),
          fingerprintVersion: printerToolsData?.fingerprint ? "PRINTERTOOLS_V1" : "",
          plataforma: navigator.platform || "",
          appKey: "DASHBOARD",
          appVersion: process.env.REACT_APP_VERSION || "1.0.0",
        }),
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
      });
      if (!response.ok) {
        return null;
      }
      const payload = await response.json();
      const result = payload?.result ?? payload;
      if (!result?.found || !result?.device) {
        return null;
      }

      if (result.isBlocked) {
        setTenantGateStatus(tenantGateStatuses.DENIED);
        setTenantGateMessage(result.message || "Equipo bloqueado.");
        return { blocked: true, offerUpgrade: result.offerUpgrade, upgradeMessage: result.upgradeMessage };
      }

      if (result.offerUpgrade) {
        setTenantGateStatus(tenantGateStatuses.DENIED);
        setTenantGateMessage(result.upgradeMessage || "Limite de dispositivos alcanzado.");
        return { blocked: true, offerUpgrade: true, upgradeMessage: result.upgradeMessage };
      }

      const device = normalizeDeviceData(result.device);
      return device;
    } catch (err) {
      return null;
    }
  };

  const saveStoredTenantDevice = async (device) => {
    const normalized = normalizeDeviceData(device);
    localStorage.setItem(arsyncTenantDeviceName, JSON.stringify(normalized));
    appendTenantDebugLog("SAVE_STORED_TENANT_DEVICE", { normalized, storage: getTenantDebugStorageSnapshot() });

    if (isMobileTenant) return;

    try {
      await fetchTenantWithTimeout(apiTenantLocalDeviceLicense, {
        method: "POST",
        body: JSON.stringify(normalized),
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
      }, 4000);
    } catch {
      // localStorage conserva el respaldo si PrinterTools no puede escribir en ese instante.
    }
  };

  const saveStoredTenantActivation = (activation) => {
    localStorage.setItem(arsyncTenantActivationName, JSON.stringify(activation));
  };

  const getStoredTenantActivation = () => {
    try {
      const raw = localStorage.getItem(arsyncTenantActivationName);
      if (isNullOrEmpty(raw)) return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  };

  const normalizeDeviceData = (device) => {
    if (!device) return device;
    return Object.entries(device).reduce((acc, [key, value]) => {
      if (typeof value !== "string") {
        acc[key] = value;
        return acc;
      }
      const trimmed = value.trim();
      switch (key) {
        case "tenantKey":
        case "licenseCode":
        case "devicePublicId":
        case "deviceKey":
        case "licenseGuid":
          acc[key] = trimmed.toUpperCase();
          break;
        case "adminEmail":
          acc[key] = trimmed;
          break;
        default:
          acc[key] = trimmed;
      }
      return acc;
    }, {});
  };

  const isStoredDeviceForLicense = (stored, licenseCode) => {
    const currentLicense = String(licenseCode ?? "").trim().toUpperCase();
    if (!stored || isNullOrEmpty(currentLicense)) return false;

    return [stored.licenseCode, stored.tenantKey, stored.licenseGuid]
      .map((value) => String(value ?? "").trim().toUpperCase())
      .some((value) => value === currentLicense);
  };

  const getStoredActivationMfaStatus = (activation) =>
    String(activation?.mfaStatusCode ?? activation?.statusCode ?? "").trim().toUpperCase();

  const canResumeStoredTenantActivation = (activation) => {
    if (!activation?.activationSessionId) return false;
    return !["PENDING_LICENSE", "CONFIRMED"].includes(getStoredActivationMfaStatus(activation));
  };

  const applyStoredTenantActivation = (activation) => {
    if (!activation?.activationSessionId) return false;

    setTenantMfaSetup(activation);
    setTenantActivationForm({
      ...getTenantActivationForm(),
      licenseGuid: { value: String(activation.licenseCode ?? "").trim().toUpperCase(), error: false },
      companyName: { value: String(activation.companyName ?? "").trim(), error: false },
      businessName: { value: String(activation.businessName ?? "").trim(), error: false },
      adminName: { value: String(activation.adminName ?? "").trim(), error: false },
      adminEmail: { value: String(activation.adminEmail ?? "").trim(), error: false },
      adminPhone: { value: String(activation.adminPhone ?? "").trim(), error: false },
    });

    if (["PENDING_LICENSE", "CONFIRMED"].includes(getStoredActivationMfaStatus(activation))) {
      setTenantGateStatus(tenantGateStatuses.MFA_CONFIRMED);
      setTenantGateMessage("Cuenta verificada. Continúa con la licencia de activación.");
      return true;
    }

    setTenantGateStatus(tenantGateStatuses.MFA_REQUIRED);
    setTenantGateMessage("Confirma el código de Google Authenticator para continuar.");
    return true;
  };

  const buildTenantFingerprint = () => {
    const screenData = window.screen
      ? `${window.screen.width}x${window.screen.height}x${window.screen.colorDepth}`
      : "";

    return [
      navigator.userAgent,
      navigator.language,
      Intl.DateTimeFormat().resolvedOptions().timeZone,
      screenData,
      window.location.hostname,
    ].filter(Boolean).join("|");
  };

  const mapTenantGateResponse = (response) => {
    if (!response) {
      return {
        status: tenantGateStatuses.DENIED,
        message: "Acceso no disponible",
        isBlocked: false,
      };
    }

    if (response.statusCode === "DEVICE_KEY_CLONED") {
      return {
        status: tenantGateStatuses.NEEDS_REASSIGNMENT,
        message: response.message || "La llave de este equipo ya está registrada en otro dispositivo.",
        isBlocked: false,
      };
    }

    if (response.isBlocked || response.statusCode === "DEVICE_BANNED") {
      return {
        status: tenantGateStatuses.DENIED,
        message: response.message || "Acceso no disponible",
        isBlocked: true,
      };
    }

    if (response.canLogin && response.tenantAccessToken) {
      return {
        status: tenantGateStatuses.AUTHORIZED,
        message: "",
        isBlocked: false,
      };
    }

    if ((response.statusCode ?? "").toUpperCase() === "TENANT_TOKEN_UNAVAILABLE") {
      return {
        status: tenantGateStatuses.DENIED,
        message: response.message ?? "No fue posible preparar el acceso.",
        isBlocked: false,
      };
    }

    if (response.requiresProvisioning) {
      return {
        status: tenantGateStatuses.PENDING,
        message: response.message ?? "Base de datos en provisionamiento.",
        isBlocked: false,
      };
    }

    if ((response.statusCode ?? "").toUpperCase() === "PENDING_APPROVAL") {
      return {
        status: tenantGateStatuses.PENDING_APPROVAL,
        message: response.message ?? "Solicitud de acceso pendiente de autorización.",
        isBlocked: false,
      };
    }

    // 18/09/2026 | Dispositivo ya aprobado pero aún sin token de acceso (flujo
    // solicitud portátil/estación). No debe caer a NEEDS_ACTIVATION ni pedir
    // recaptura: se conserva en PENDING_APPROVAL para que Reintentar valide de
    // nuevo y obtenga el token. No afecta activación nueva PC (MFA/redeem) ni
    // el caso canLogin+token que ya retornó AUTHORIZED arriba.
    if (["APPROVED", "ACTIVE", "AUTHORIZED"].includes((response.statusCode ?? "").toUpperCase())) {
      return {
        status: tenantGateStatuses.PENDING_APPROVAL,
        message: response.message ?? "Dispositivo aprobado. Presiona Reintentar para activar el acceso.",
        isBlocked: false,
      };
    }

    if (response.requiresActivation) {
      return {
        status: tenantGateStatuses.NEEDS_ACTIVATION,
        message: "Activación requerida",
        isBlocked: false,
      };
    }

    if (response.requiresPayment || response.requiresVersionUpdate) {
      return {
        status: tenantGateStatuses.DENIED,
        message: "Acceso no disponible",
        isBlocked: false,
      };
    }

    if (response.databaseNotFound || (response.statusCode ?? "").toUpperCase() === "DATABASE_NOT_FOUND") {
      return {
        status: tenantGateStatuses.DATABASE_NOT_FOUND,
        message: response.message ?? "Base de datos no disponible. Se requiere reactivar el equipo.",
        isBlocked: false,
      };
    }

    return {
      status: tenantGateStatuses.NEEDS_ACTIVATION,
      message: "Activación requerida",
      isBlocked: false,
    };
  };

  const validateTenantDevice = async (device, printerFingerprint = null) => {
    setTenantGateStatus(tenantGateStatuses.VALIDATING);
    setTenantGateMessage("");

    if (device?.tenantKey && device?.devicePublicId && device?.deviceKey) {
      setTenantAdminDevice(device);
    }

    let nombreEquipo = (window.location.hostname ?? "").trim();
    try {
      const machineInfo = await getMachineNameFromPrinterTools();
      if (machineInfo) {
        nombreEquipo = machineInfo.trim();
      }
    } catch {}

    if (device?.fromPrinterTools) {
      const responseApi = await fetch(apiTenantLocalDeviceValidate, {
        method: "POST",
        body: JSON.stringify({
          seguridadUrl: serverSeguridad,
          appKey: "DASHBOARD",
          appVersion: process.env.REACT_APP_VERSION || "1.0.0",
          nombreEquipo: String(device.machineName ?? nombreEquipo ?? "").trim(),
          fingerprint: printerFingerprint || buildTenantFingerprint(),
          devicePublicId: String(device.devicePublicId ?? "").trim().toUpperCase(),
          tenantKey: String(device.tenantKey ?? "").trim().toUpperCase(),
        }),
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
      });

      if (!responseApi.ok) {
        setTenantGateStatus(tenantGateStatuses.DENIED);
        setTenantGateMessage("Acceso no disponible");
        return tenantGateStatuses.DENIED;
      }

      const payload = await responseApi.json();
      const response = payload?.result ?? payload;
      const gate = mapTenantGateResponse(response);

      if (gate.status === tenantGateStatuses.DATABASE_NOT_FOUND) {
        handleClearTenantDeviceData();
        setTenantGateStatus(tenantGateStatuses.NEEDS_ACTIVATION);
        setTenantGateMessage("Base de datos no disponible. Se requiere una nueva activación.");
        return tenantGateStatuses.NEEDS_ACTIVATION;
      }

      setTenantCorrelationId(response?.correlationId ?? "");
      setTenantAdminStatus(response);
      if (response?.tenantAccessToken) {
        localStorage.setItem(tenantAccessTokenName, response.tenantAccessToken);
      } else if ((response?.statusCode ?? "").toUpperCase() === "TENANT_TOKEN_UNAVAILABLE") {
        localStorage.removeItem(tenantAccessTokenName);
      }
      const finalStatus = gate.status === tenantGateStatuses.PENDING || gate.status === tenantGateStatuses.ADMIN_DASHBOARD
        ? tenantGateStatuses.NEEDS_ACTIVATION
        : gate.status;
      setTenantGateStatus(finalStatus);
      setTenantGateMessage(finalStatus === tenantGateStatuses.NEEDS_ACTIVATION ? (gate.message || "Activación requerida") : gate.message);
      return finalStatus;
    }

    const request = {
      tenantKey: String(device.tenantKey ?? "").trim().toUpperCase(),
      devicePublicId: String(device.devicePublicId ?? "").trim().toUpperCase(),
      deviceKey: String(device.deviceKey ?? "").trim().toUpperCase(),
      fingerprint: printerFingerprint || buildTenantFingerprint(),
      nombreEquipo: String(nombreEquipo ?? "").trim(),
      appKey: "DASHBOARD",
      appVersion: process.env.REACT_APP_VERSION || "1.0.0",
    };

    const validateUrl = isMobileTenant ? apiTenantMobileDeviceValidate : apiTenantDeviceValidate;
    appendTenantDebugLog("VALIDATE_TENANT_DEVICE_SEND", {
      url: validateUrl,
      method: "POST",
      body: request,
      storageBefore: getTenantDebugStorageSnapshot(),
    });

    let responseApi;
    try {
      responseApi = await fetch(validateUrl, {
        method: "POST",
        body: JSON.stringify(request),
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
      });
    } catch (error) {
      appendTenantDebugLog("VALIDATE_TENANT_DEVICE_FETCH_ERROR", {
        errorName: error?.name,
        errorMessage: error?.message,
        errorStack: error?.stack,
        storageAfterError: getTenantDebugStorageSnapshot(),
      });
      throw error;
    }

    const { responseText, payload, parseError } = await readTenantDebugResponse(responseApi);
    appendTenantDebugLog("VALIDATE_TENANT_DEVICE_RESPONSE", {
      url: validateUrl,
      status: responseApi.status,
      statusText: responseApi.statusText,
      ok: responseApi.ok,
      headers: fromEntries(responseApi.headers.entries()),
      responseText,
      parseError,
      payload,
      storageAfterResponse: getTenantDebugStorageSnapshot(),
    });


    if (!responseApi.ok) {
      setTenantGateStatus(tenantGateStatuses.DENIED);
      setTenantGateMessage("Acceso no disponible");
      return tenantGateStatuses.DENIED;
    }

    const response = payload?.result ?? payload;
    const gate = mapTenantGateResponse(response);
    appendTenantDebugLog("VALIDATE_TENANT_DEVICE_GATE", {
      response,
      gate,
      storageAfterGate: getTenantDebugStorageSnapshot(),
    });

    if (gate.status === tenantGateStatuses.DATABASE_NOT_FOUND) {
      handleClearTenantDeviceData();
      setTenantGateStatus(tenantGateStatuses.NEEDS_ACTIVATION);
      setTenantGateMessage("Base de datos no disponible. Se requiere una nueva activación.");
      return tenantGateStatuses.NEEDS_ACTIVATION;
    }

    setTenantCorrelationId(response?.correlationId ?? "");
    setTenantAdminStatus(response);
    if (response?.tenantAccessToken) {
      localStorage.setItem(tenantAccessTokenName, response.tenantAccessToken);
    } else if ((response?.statusCode ?? "").toUpperCase() === "TENANT_TOKEN_UNAVAILABLE") {
      localStorage.removeItem(tenantAccessTokenName);
    }
    setTenantGateStatus(gate.status);
    setTenantGateMessage(gate.message);

    if (isMobileTenant) {
      // 18/09/2026 | Solo persistir estados que conservan la solicitud aprobada
      // (AUTHORIZED / PENDING_APPROVAL). Persistir NEEDS_ACTIVATION o DENIED aquí
      // corrompía el registro local aprobado y obligaba a recapturar todo.
      const stored = getBrowserStoredTenantDevice();
      if (stored && (gate.status === tenantGateStatuses.AUTHORIZED || gate.status === tenantGateStatuses.PENDING_APPROVAL)) {
        stored.status = gate.status;
        localStorage.setItem(arsyncTenantDeviceName, JSON.stringify(stored));
      }
    }

    return gate.status;
  };

  const handleTenantGateBootstrap = async () => {
    if (bootstrapPromiseRef.current) {
      return bootstrapPromiseRef.current;
    }

    bootstrapPromiseRef.current = (async () => {
      setTenantGateStatus(tenantGateStatuses.BOOTSTRAP);
      appendTenantDebugLog("TENANT_GATE_BOOTSTRAP_START", {
        isMobileTenant,
        storage: getTenantDebugStorageSnapshot(),
      });

      const pendingActivation = getStoredTenantActivation();
      if (pendingActivation?.activationSessionId) {
        appendTenantDebugLog("TENANT_GATE_BOOTSTRAP_STORED_ACTIVATION", {
          pendingActivation,
          storage: getTenantDebugStorageSnapshot(),
        });
        if (canResumeStoredTenantActivation(pendingActivation) && applyStoredTenantActivation(pendingActivation)) {
          return false;
        }
        appendTenantDebugLog("TENANT_GATE_BOOTSTRAP_STORED_ACTIVATION_NEEDS_SERVER_CHECK", {
          mfaStatusCode: getStoredActivationMfaStatus(pendingActivation),
          storage: getTenantDebugStorageSnapshot(),
        });
      }

      if (isMobileTenant) {
        setTenantGateMessage("Validando dispositivo portatil...");
        const mobileDevice = getBrowserStoredTenantDevice();
        appendTenantDebugLog("TENANT_GATE_BOOTSTRAP_MOBILE_DEVICE", {
          mobileDevice,
          storage: getTenantDebugStorageSnapshot(),
        });

        if (!mobileDevice) {
          localStorage.removeItem(tenantAccessTokenName);
          setTenantGateStatus(tenantGateStatuses.NEEDS_ACTIVATION);
          setTenantGateMessage("Activa este dispositivo o recupera una activación existente con Google Authenticator.");
          appendTenantDebugLog("TENANT_GATE_BOOTSTRAP_NO_MOBILE_DEVICE", {
            storage: getTenantDebugStorageSnapshot(),
          });
          return false;
        }

        try {
          const status = await validateTenantDevice(mobileDevice, mobileDevice.fingerprint || "");
          appendTenantDebugLog("TENANT_GATE_BOOTSTRAP_MOBILE_VALIDATE_STATUS", {
            status,
            storage: getTenantDebugStorageSnapshot(),
          });
          if (status === tenantGateStatuses.AUTHORIZED) {
            setTenantGateStatus(tenantGateStatuses.AUTHORIZED);
            setTenantGateMessage("");
            return true;
          }
          if (status === tenantGateStatuses.PENDING_APPROVAL) {
            return false;
          }
        } catch (err) {
        }
        appendTenantDebugLog("TENANT_GATE_BOOTSTRAP_MOBILE_VALIDATE_FAILED", {
          storage: getTenantDebugStorageSnapshot(),
        });

        localStorage.removeItem(tenantAccessTokenName);
        // 18/09/2026 | Conservar datos registrados en el formulario para no
        // obligar a recapturar todo (licencia, contacto y alias ya existen en
        // el dispositivo local). La clave de aceptación NO se requiere aquí:
        // solo aplica a activación nueva de licencia, no a Reintentar.
        setTenantActivationForm((prev) => {
          const current = normalizeTenantActivationForm(prev);
          return {
            ...current,
            licenseGuid: { value: current.licenseGuid.value || String(mobileDevice?.licenseCode || mobileDevice?.tenantKey || "").trim().toUpperCase(), error: false },
            adminName: { value: current.adminName.value || String(mobileDevice?.adminName || "").trim(), error: false },
            adminEmail: { value: current.adminEmail.value || String(mobileDevice?.adminEmail || "").trim(), error: false },
            deviceAlias: { value: current.deviceAlias.value || String(mobileDevice?.deviceAlias || "").trim(), error: false },
          };
        });
        setTenantGateStatus(tenantGateStatuses.NEEDS_ACTIVATION);
        setTenantGateMessage("La sesión local no pudo validarse. Recupera el acceso con los datos registrados y Google Authenticator.");
        return false;
      }

      setTenantGateMessage("Verificando PrinterTools...");

      const printerToolsAvailable = await validatePrinterToolsLocal();
      if (!printerToolsAvailable) {
        localStorage.removeItem(tenantAccessTokenName);
        setTenantGateStatus(tenantGateStatuses.PRINTERTOOLS_REQUIRED);
        setTenantGateMessage("Instala PrinterTools para preparar este equipo.");
        return false;
      }

      const printerToolsData = await getPrinterToolsFingerprint();
      const printerFingerprint = printerToolsData?.fingerprint || "";
      const machineName = printerToolsData?.machineName || await getMachineNameFromPrinterTools();
      if (isNullOrEmpty(machineName)) {
        localStorage.removeItem(tenantAccessTokenName);
        setTenantGateStatus(tenantGateStatuses.PRINTERTOOLS_REQUIRED);
        setTenantGateMessage("PrinterTools no entrego el nombre del equipo. Abre PrinterTools y reintenta.");
        return false;
      }

      setTenantGateStatus(tenantGateStatuses.LOOKUP_BY_NAME);
      setTenantGateMessage(`Validando equipo ${machineName} en Seguridad...`);

      const foundDevice = await findTenantDeviceByName(machineName, printerToolsData);

      if (!foundDevice) {
        handleClearTenantDeviceData();
        setTenantActivationForm({
          ...getTenantActivationForm(),
          deviceAlias: { value: machineName, error: false },
        });
        setTenantGateStatus(tenantGateStatuses.NEEDS_ACTIVATION);
        setTenantGateMessage("Equipo no registrado. Solicita acceso si la licencia ya está activa o activa una licencia nueva con QR.");
        return false;
      }

      if (foundDevice.blocked) {
        localStorage.removeItem(tenantAccessTokenName);
        return false;
      }

      await saveStoredTenantDevice(foundDevice);

      try {
        const deviceStatus = await validateTenantDevice({
          ...foundDevice,
          machineName,
        }, printerFingerprint);

        if (deviceStatus === tenantGateStatuses.AUTHORIZED) {
          localStorage.removeItem(arsyncTenantActivationName);
          setTenantMfaSetup(null);
          setTenantGateStatus(tenantGateStatuses.AUTHORIZED);
          setTenantGateMessage("");
          return true;
        }

        if (deviceStatus === tenantGateStatuses.DENIED) {
          localStorage.removeItem(tenantAccessTokenName);
          return false;
        }

        // 18/09/2026 | Estación aprobada vía solicitud de acceso pero aún sin
        // token: conservar PENDING_APPROVAL (ya fijado por validate) en lugar
        // de caer a NEEDS_ACTIVATION y pedir recaptura. No toca AUTHORIZED,
        // DENIED ni PENDING existentes.
        if (deviceStatus === tenantGateStatuses.PENDING_APPROVAL) {
          return false;
        }

        if (deviceStatus === tenantGateStatuses.PENDING) {
          try {
            const provDeviceKey = String(foundDevice?.deviceKey ?? foundDevice?.tenantKey ?? "").trim().toUpperCase();
            const provTenantKey = String(foundDevice?.tenantKey ?? "").trim().toUpperCase();
            const provDevicePublicId = String(foundDevice?.devicePublicId ?? "").trim().toUpperCase();
            if (provTenantKey && provDevicePublicId && provDeviceKey) {
              const provApi = await fetch(apiTenantProvisioningRun, {
                method: "POST",
                body: JSON.stringify({
                  tenantKey: provTenantKey,
                  devicePublicId: provDevicePublicId,
                  deviceKey: provDeviceKey,
                  appKey: "DASHBOARD",
                  appVersion: process.env.REACT_APP_VERSION || "1.0.0",
                }),
                headers: { "Content-Type": "application/json", "Accept": "application/json" },
              });
              const provBody = await provApi.json();
            }
          } catch (e) {
          }
          return false;
        }

      } catch (err) {
        localStorage.removeItem(tenantAccessTokenName);
        setTenantGateStatus(tenantGateStatuses.DENIED);
        setTenantGateMessage("Acceso no disponible");
        return false;
      }

      localStorage.removeItem(tenantAccessTokenName);
      setTenantActivationForm(prev => {
        const current = normalizeTenantActivationForm(prev);
        return {
          ...current,
          licenseGuid: { value: current.licenseGuid.value || String(foundDevice?.licenseCode || foundDevice?.tenantKey || "").trim().toUpperCase(), error: false },
          companyName: { value: current.companyName.value || String(foundDevice?.companyName || "").trim(), error: false },
          businessName: { value: current.businessName.value || String(foundDevice?.businessName || "").trim(), error: false },
          adminName: { value: current.adminName.value || String(foundDevice?.adminName || "").trim(), error: false },
          adminEmail: { value: current.adminEmail.value || String(foundDevice?.adminEmail || "").trim(), error: false },
          adminPhone: { value: current.adminPhone.value || String(foundDevice?.adminPhone || "").trim(), error: false },
        };
      });
      setTenantGateStatus(tenantGateStatuses.NEEDS_ACTIVATION);
      setTenantGateMessage("Equipo encontrado, pero no está activo. Ingresa una licencia válida para continuar.");
      return false;
    })();

    return bootstrapPromiseRef.current;
  };

  const clearLogoutTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const clearRefreshTimer = () => {
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  };

  const reportCrossTabActivity = () => {
    const now = Date.now();
    if (now - lastCrossTabWriteRef.current < crossTabWriteMs) return;
    lastCrossTabWriteRef.current = now;
    try {
      localStorage.setItem(crossTabActivityKey, String(now));
    } catch {}
  };

  const readCrossTabActivity = () => {
    try {
      return Number(localStorage.getItem(crossTabActivityKey) || 0) || 0;
    } catch {
      return 0;
    }
  };

  const handleLogoutTimer = () => {
    clearLogoutTimer();
    timerRef.current = setTimeout(() => {
      // Doble verificación: si otra pestaña reportó actividad reciente, rearmar
      // en vez de cerrar (cubre storage events perdidos por throttle).
      const last = readCrossTabActivity();
      if (last > 0 && Date.now() - last < tiempoCierreSession) {
        handleLogoutTimer();
        return;
      }
      clearRefreshTimer();
      events.forEach((item) => {
        window.removeEventListener(item, handleSesionActivity);
      });
      window.removeEventListener("storage", handleCrossTabStorage);
      handleClickCerrarSesion();
    }, tiempoCierreSession);
  };

  const handleSesionActivity = () => {
    reportCrossTabActivity();
    handleLogoutTimer();
  };

  // Actividad en OTRA pestaña: rearmar el timer propio sin escribir de vuelta
  // (evita ping-pong de storage events entre pestañas).
  const handleCrossTabStorage = (event) => {
    if (event?.key !== crossTabActivityKey) return;
    handleLogoutTimer();
  };

  useEffect(() => {
    let mounted = true;

    const run = async ({
      navigateAfterSave = true,
      mostrarLoader = true,
    } = {}) => {
      const tenantAuthorized = await handleTenantGateBootstrap();
      if (!mounted) return;
      if (!tenantAuthorized) return;

      if (location.pathname === routeTenantAdminPasswordRecovery) return;

      const { accessToken } = getSession();
      if (isNullOrEmpty(accessToken)) {
        if (mounted) navigate(routeLogin, { replace: true });
        return;
      }

      const result = await refreshSession({ navigateAfterSave, mostrarLoader });
      if (!mounted) return;

      // refreshSession ya guardó y navegó en éxito. Solo queda redirigir a Login
      // cuando el arranque (navigateAfterSave) no pudo recuperar la sesión.
      if (!result.ok && navigateAfterSave) {
        navigate(routeLogin, { replace: true });
      }
    };

    run();

    if (location.pathname !== routeTenantAdminPasswordRecovery) {
      events.forEach((item) => {
        window.addEventListener(item, handleSesionActivity);
      });
      window.addEventListener("storage", handleCrossTabStorage);
      handleLogoutTimer();
      startPreventiveRefresh();
    }

    return () => {
      mounted = false;
      clearLogoutTimer();
      clearRefreshTimer();
      refreshPromiseRef.current = null;
      isLoggingOutRef.current = false;
      events.forEach((item) => {
        window.removeEventListener(item, handleSesionActivity);
      });
      window.removeEventListener("storage", handleCrossTabStorage);
    };
  }, []);

  const handleTenantActivationFormChange = (e) => {
    const currentForm = normalizeTenantActivationForm(tenantActivationForm);
    const value = e.target.name === "licenseGuid"
      ? formatTenantActivationGuid(e.target.value)
      : e.target.value;

    setTenantActivationForm({
      ...currentForm,
      [e.target.name]: {
        value,
        error: false,
      },
    });
  };

  const handleRequestMobileDeviceAccess = async (e) => {
    e.preventDefault();

    if (!isMobileTenant) return;

    const currentForm = normalizeTenantActivationForm(tenantActivationForm);
    const formattedLicense = formatTenantActivationGuid(currentForm.licenseGuid.value);
    const form = {
      ...currentForm,
      licenseGuid: {
        ...currentForm.licenseGuid,
        value: formattedLicense,
        error: !tenantActivationGuidPattern.test(formattedLicense),
      },
      deviceAlias: {
        ...currentForm.deviceAlias,
        error: isNullOrEmpty(currentForm.deviceAlias.value),
      },
      adminEmail: {
        ...currentForm.adminEmail,
        error: isNullOrEmpty(currentForm.adminEmail.value) || !currentForm.adminEmail.value.includes("@"),
      },
      adminName: {
        ...currentForm.adminName,
        error: isNullOrEmpty(currentForm.adminName.value),
      },
    };

    setTenantActivationForm(form);
    if (form.licenseGuid.error || form.deviceAlias.error || form.adminEmail.error || form.adminName.error) {
      setTenantGateMessage("Captura licencia, apodo o teléfono, correo y nombre de usuario.");
      return;
    }

    const licenseCode = form.licenseGuid.value.trim().toUpperCase();
    const stored = await getStoredTenantDevice();
    const storedForLicense = isStoredDeviceForLicense(stored, licenseCode) ? stored : null;
    const device = normalizeDeviceData({
      tenantKey: licenseCode,
      licenseCode,
      devicePublicId: (storedForLicense?.devicePublicId ?? createSecureId()).toString(),
      deviceKey: (storedForLicense?.deviceKey ?? createDeviceSecret()).toString(),
      adminEmail: form.adminEmail.value,
      adminName: form.adminName.value,
      deviceAlias: form.deviceAlias.value,
      fingerprint: buildTenantFingerprint(),
      status: "PENDING_APPROVAL",
      pendingAccess: true,
      createdAt: storedForLicense?.createdAt ?? new Date().toISOString(),
    });

    const requestBody = {
      licenseCode: device.licenseCode,
      devicePublicId: device.devicePublicId,
      deviceKey: device.deviceKey,
      fingerprint: device.fingerprint,
      fingerprintVersion: "MOBILE_BROWSER_V1",
      plataforma: navigator.userAgent || navigator.platform || "MOBILE_BROWSER",
      nombreDispositivo: device.deviceAlias,
      telefonoDispositivo: device.deviceAlias,
      correo: device.adminEmail,
      nombreUsuario: device.adminName,
      appKey: "DASHBOARD",
      appVersion: process.env.REACT_APP_VERSION || "1.0.0",
    };

    appendTenantDebugLog("REQUEST_MOBILE_ACCESS_PREPARED", {
      device,
      requestBody,
      storageBeforeRequest: getTenantDebugStorageSnapshot(),
    });

    try {
      setTenantGateStatus(tenantGateStatuses.VALIDATING);
      setTenantGateMessage("Enviando solicitud de acceso al propietario de la licencia...");
      appendTenantDebugLog("REQUEST_MOBILE_ACCESS_SEND", {
        url: apiTenantMobileDeviceAccessRequest,
        method: "POST",
        timeoutMs: 20000,
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
        body: requestBody,
        storageBeforeFetch: getTenantDebugStorageSnapshot(),
      });

      const responseApi = await fetchTenantWithTimeout(apiTenantMobileDeviceAccessRequest, {
        method: "POST",
        body: JSON.stringify(requestBody),
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
      }, 20000);

      const { responseText, payload, parseError } = await readTenantDebugResponse(responseApi);
      const result = payload?.result ?? payload;
      appendTenantDebugLog("REQUEST_MOBILE_ACCESS_RESPONSE", {
        url: apiTenantMobileDeviceAccessRequest,
        status: responseApi.status,
        statusText: responseApi.statusText,
        ok: responseApi.ok,
        headers: fromEntries(responseApi.headers.entries()),
        responseText,
        parseError,
        payload,
        result,
        storageAfterResponse: getTenantDebugStorageSnapshot(),
      });

      if (!responseApi.ok || payload?.hasError) {
        setTenantGateStatus(tenantGateStatuses.NEEDS_ACTIVATION);
        setTenantGateMessage(payload?.message ?? payload?.Message ?? result?.message ?? `No fue posible solicitar acceso (${responseApi.status}).`);
        return;
      }

      const mobileAccessStatus = String(result?.statusCode ?? result?.deviceStatus ?? "PENDING_APPROVAL").trim().toUpperCase();
      const storedMobileDevice = normalizeDeviceData({
        ...device,
        tenantKey: result?.tenantKey ?? device.tenantKey,
        licenseCode: result?.licenseCode ?? device.licenseCode,
        requestId: result?.requestId,
        status: mobileAccessStatus,
        pendingAccess: mobileAccessStatus === "PENDING_APPROVAL",
      });

      setTenantCorrelationId(result?.correlationId ?? "");
      await saveStoredTenantDevice(storedMobileDevice);

      if (mobileAccessStatus === "APPROVED" || mobileAccessStatus === "ACTIVE") {
        appendTenantDebugLog("REQUEST_MOBILE_ACCESS_APPROVED_VALIDATE", {
          result,
          storedMobileDevice,
          storageAfterSave: getTenantDebugStorageSnapshot(),
        });
        setTenantGateStatus(tenantGateStatuses.VALIDATING);
        setTenantGateMessage("Dispositivo aprobado. Validando acceso...");

        const status = await validateTenantDevice(storedMobileDevice, storedMobileDevice.fingerprint || "");
        if (status === tenantGateStatuses.AUTHORIZED) {
          navigate(routeLogin, { replace: true });
          return;
        }
        if (status === tenantGateStatuses.PENDING_APPROVAL) {
          setTenantGateMessage("Solicitud enviada. El propietario debe aprobar este dispositivo desde el dashboard administrativo.");
        }
        return;
      }

      setTenantGateStatus(tenantGateStatuses.PENDING_APPROVAL);
      setTenantGateMessage(result?.message ?? "Solicitud enviada. El propietario debe aprobar este dispositivo desde el dashboard administrativo.");
      appendTenantDebugLog("REQUEST_MOBILE_ACCESS_STORED_PENDING", {
        result,
        storageAfterSave: getTenantDebugStorageSnapshot(),
      });
    } catch (error) {
      appendTenantDebugLog("REQUEST_MOBILE_ACCESS_ERROR", {
        errorName: error?.name,
        errorMessage: error?.message,
        errorStack: error?.stack,
        storageAfterError: getTenantDebugStorageSnapshot(),
      });
      setTenantGateStatus(tenantGateStatuses.NEEDS_ACTIVATION);
      setTenantGateMessage(`No fue posible enviar la solicitud: ${error?.message ?? "sin conexión"}`);
    }
  };

  const handleRequestWorkstationAccess = async (e) => {
    e?.preventDefault?.();

    if (isMobileTenant) return;

    const printerToolsAvailable = await validatePrinterToolsLocal();
    if (!printerToolsAvailable) {
      setTenantGateStatus(tenantGateStatuses.PRINTERTOOLS_REQUIRED);
      setTenantGateMessage("PrinterTools es requerido para identificar esta estacion de trabajo.");
      return;
    }

    const printerToolsData = await getPrinterToolsFingerprint();
    const printerFingerprint = String(printerToolsData?.fingerprint ?? "").trim();
    const machineName = String(printerToolsData?.machineName ?? await getMachineNameFromPrinterTools() ?? "").trim();

    if (!printerFingerprint || isNullOrEmpty(machineName)) {
      setTenantGateStatus(tenantGateStatuses.PRINTERTOOLS_REQUIRED);
      setTenantGateMessage("PrinterTools no entrego nombre o huella del equipo. Abre PrinterTools y reintenta.");
      return;
    }

    const currentForm = normalizeTenantActivationForm(tenantActivationForm);
    const formattedLicense = formatTenantActivationGuid(currentForm.licenseGuid.value);
    const form = {
      ...currentForm,
      licenseGuid: {
        ...currentForm.licenseGuid,
        value: formattedLicense,
        error: !tenantActivationGuidPattern.test(formattedLicense),
      },
      adminEmail: {
        ...currentForm.adminEmail,
        error: isNullOrEmpty(currentForm.adminEmail.value) || !currentForm.adminEmail.value.includes("@"),
      },
      adminName: {
        ...currentForm.adminName,
        error: isNullOrEmpty(currentForm.adminName.value),
      },
      deviceAlias: {
        ...currentForm.deviceAlias,
        value: String(currentForm.deviceAlias.value || machineName).trim(),
        error: false,
      },
    };

    setTenantActivationForm(form);
    if (form.licenseGuid.error || form.adminEmail.error || form.adminName.error) {
      setTenantGateMessage("Captura licencia, correo y nombre del administrador para solicitar acceso.");
      return;
    }

    const licenseCode = form.licenseGuid.value.trim().toUpperCase();
    const stored = await getStoredTenantDevice();
    const storedForLicense = isStoredDeviceForLicense(stored, licenseCode) ? stored : null;
    const deviceAlias = String(form.deviceAlias.value || machineName).trim();
    const device = normalizeDeviceData({
      tenantKey: licenseCode,
      licenseCode,
      devicePublicId: (storedForLicense?.devicePublicId ?? createSecureId()).toString(),
      deviceKey: (storedForLicense?.deviceKey ?? createDeviceSecret()).toString(),
      adminEmail: form.adminEmail.value,
      adminName: form.adminName.value,
      deviceAlias,
      fingerprint: printerFingerprint,
      status: "PENDING_APPROVAL",
      pendingAccess: true,
      createdAt: storedForLicense?.createdAt ?? new Date().toISOString(),
    });

    const requestBody = {
      licenseCode: device.licenseCode,
      devicePublicId: device.devicePublicId,
      deviceKey: device.deviceKey,
      fingerprint: device.fingerprint,
      fingerprintVersion: "PRINTERTOOLS_V1",
      plataforma: navigator.platform || navigator.userAgent || "WEB_WORKSTATION",
      nombreDispositivo: deviceAlias,
      telefonoDispositivo: "",
      correo: device.adminEmail,
      nombreUsuario: device.adminName,
      appKey: "DASHBOARD",
      appVersion: process.env.REACT_APP_VERSION || "1.0.0",
    };

    try {
      setTenantGateStatus(tenantGateStatuses.VALIDATING);
      setTenantGateMessage("Enviando solicitud de acceso al propietario de la licencia...");

      const responseApi = await fetchTenantWithTimeout(apiTenantMobileDeviceAccessRequest, {
        method: "POST",
        body: JSON.stringify(requestBody),
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
      }, 20000);

      const { payload } = await readTenantDebugResponse(responseApi);
      const result = payload?.result ?? payload;

      if (!responseApi.ok || payload?.hasError) {
        setTenantGateStatus(tenantGateStatuses.NEEDS_ACTIVATION);
        setTenantGateMessage(payload?.message ?? payload?.Message ?? result?.message ?? `No fue posible solicitar acceso (${responseApi.status}). Si la licencia es nueva, activala con QR.`);
        return;
      }

      await saveStoredTenantDevice({
        ...device,
        tenantKey: result?.tenantKey ?? device.tenantKey,
        licenseCode: result?.licenseCode ?? device.licenseCode,
        requestId: result?.requestId,
        status: result?.statusCode ?? "PENDING_APPROVAL",
        pendingAccess: true,
      });

      setTenantCorrelationId(result?.correlationId ?? "");
      setTenantGateStatus(tenantGateStatuses.PENDING_APPROVAL);
      setTenantGateMessage(result?.message ?? "Solicitud enviada. El propietario debe aprobar esta estacion desde el dashboard administrativo.");
    } catch (error) {
      setTenantGateStatus(tenantGateStatuses.NEEDS_ACTIVATION);
      setTenantGateMessage(`No fue posible enviar la solicitud: ${error?.message ?? "sin conexion"}`);
    }
  };
  const handleSubmitTenantActivation = async (e) => {
    e.preventDefault();

    if (isMobileTenant) {
      await handleRequestMobileDeviceAccess(e);
      return;
    }

    if (!isMobileTenant) {
      const printerToolsAvailable = await validatePrinterToolsLocal();
      if (!printerToolsAvailable) {
        setTenantGateStatus(tenantGateStatuses.PRINTERTOOLS_REQUIRED);
        setTenantGateMessage("PrinterTools es requerido para activar este equipo. Instalalo e intenta de nuevo.");
        return;
      }
    }

    const currentForm = normalizeTenantActivationForm(tenantActivationForm);
    const form = {
      ...currentForm,
      licenseGuid: {
        ...currentForm.licenseGuid,
        value: formatTenantActivationGuid(currentForm.licenseGuid.value),
        error: !tenantActivationGuidPattern.test(formatTenantActivationGuid(currentForm.licenseGuid.value)),
      },
      adminName: {
        ...currentForm.adminName,
        error: isNullOrEmpty(currentForm.adminName.value),
      },
      companyName: {
        ...currentForm.companyName,
        error: isNullOrEmpty(currentForm.companyName.value) || currentForm.companyName.value.trim().length > 30,
      },
      businessName: {
        ...currentForm.businessName,
        error: isNullOrEmpty(currentForm.businessName.value),
      },
      adminEmail: {
        ...currentForm.adminEmail,
        error: isNullOrEmpty(currentForm.adminEmail.value) || !currentForm.adminEmail.value.includes("@"),
      },
      adminPhone: {
        ...currentForm.adminPhone,
        error: isNullOrEmpty(currentForm.adminPhone.value),
      },
      deviceAlias: {
        ...currentForm.deviceAlias,
        error: isMobileTenant && isNullOrEmpty(currentForm.deviceAlias.value),
      },
      adminPassword: {
        ...currentForm.adminPassword,
        error: isNullOrEmpty(currentForm.adminPassword.value) || currentForm.adminPassword.value.length < 8,
      },
      adminPasswordConfirm: {
        ...currentForm.adminPasswordConfirm,
        error: currentForm.adminPasswordConfirm.value !== currentForm.adminPassword.value,
      },
    };

    setTenantActivationForm(form);
    if (isErrorForm(form)) {
      setTenantGateMessage("Revisa los campos marcados antes de continuar.");
      return;
    }

    const licenseCode = form.licenseGuid.value.trim().toUpperCase();
    const stored = await getStoredTenantDevice();
    const storedForLicense = isStoredDeviceForLicense(stored, licenseCode) ? stored : null;
    const device = {
      tenantKey: licenseCode,
      licenseCode,
      companyName: currentForm.companyName.value.trim(),
      businessName: currentForm.businessName.value.trim(),
      adminName: currentForm.adminName.value.trim(),
      adminEmail: currentForm.adminEmail.value.trim(),
      adminPhone: currentForm.adminPhone.value.trim(),
      deviceAlias: currentForm.deviceAlias.value.trim(),
      devicePublicId: (storedForLicense?.devicePublicId ?? createSecureId()).toString().trim().toUpperCase(),
      deviceKey: (storedForLicense?.deviceKey ?? createDeviceSecret()).toString().trim().toUpperCase(),
      createdAt: storedForLicense?.createdAt ?? new Date().toISOString(),
    };

    try {
      if (isNullOrEmpty(apiTenantMfaSetup) || apiTenantMfaSetup.includes("undefined")) {
        setTenantGateStatus(tenantGateStatuses.DENIED);
        setTenantGateMessage("Endpoint tenant no configurado. Reinicia el frontend con start:Local.");
        return;
      }

      setTenantGateStatus(tenantGateStatuses.VALIDATING);
      const request = {
        nombre: device.adminName,
        correo: device.adminEmail,
        telefono: device.adminPhone,
        empresa: device.companyName,
        razonSocial: device.businessName,
        nombreComercial: device.companyName,
        adminPassword: currentForm.adminPassword.value,
        licenseCode: device.licenseCode,
        claveActivacion: currentForm.claveActivacion.value.trim(),
      };

      const responseApi = await fetch(apiTenantMfaSetup, {
        method: "POST",
        body: JSON.stringify(request),
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
      });

      if (!responseApi.ok) {
        const errorPayload = await responseApi.json().catch(() => null);
        console.error("[ACTIVATION][STEP2] ERROR responseApi.ok=false", { status: responseApi.status, statusText: responseApi.statusText, errorPayload });
        const errorMessage = errorPayload?.message ?? errorPayload?.Message ?? errorPayload?.mensaje;
        setTenantGateStatus(tenantGateStatuses.NEEDS_ACTIVATION);
        setTenantGateMessage(errorMessage ?? `No fue posible iniciar MFA (${responseApi.status})`);
        return;
      }

      const payload = await responseApi.json();
      const response = payload?.result ?? payload;
      const activation = {
        ...device,
        activationSessionId: response?.activationSessionId,
        manualEntryKey: response?.manualEntryKey,
        otpAuthUri: response?.otpAuthUri,
        mfaStatusCode: response?.statusCode,
      };

      saveStoredTenantActivation(activation);
      setTenantMfaSetup(activation);
      setTenantCorrelationId(response?.correlationId ?? "");
      setTenantGateStatus(response?.statusCode === "PENDING_LICENSE"
        ? tenantGateStatuses.MFA_CONFIRMED
        : tenantGateStatuses.MFA_REQUIRED);
      setTenantGateMessage(response?.message ?? "Configura Google Authenticator para continuar.");
    } catch (error) {
      setTenantGateStatus(tenantGateStatuses.NEEDS_ACTIVATION);
      setTenantGateMessage(`Activación pendiente: ${error?.message ?? "sin conexión"}`);
    }
  };

  const handleConfirmTenantMfa = async (e) => {
    e.preventDefault();

    const activation = tenantMfaSetup ?? getStoredTenantActivation();
    const currentForm = normalizeTenantActivationForm(tenantActivationForm);
    const code = currentForm.mfaCode.value.trim();
    const form = {
      ...currentForm,
      mfaCode: {
        ...currentForm.mfaCode,
        error: isNullOrEmpty(code) || code.length !== 6,
      },
    };

    setTenantActivationForm(form);
    if (isErrorForm(form)) {
      setTenantGateMessage("Captura el código de 6 dígitos de Google Authenticator.");
      return;
    }

    if (!activation?.activationSessionId) {
      setTenantGateStatus(tenantGateStatuses.NEEDS_ACTIVATION);
      setTenantGateMessage("Inicia nuevamente la activación.");
      return;
    }

    try {
      setTenantGateStatus(tenantGateStatuses.VALIDATING);
      const responseApi = await fetch(apiTenantMfaConfirm, {
        method: "POST",
        body: JSON.stringify({
          activationSessionId: activation.activationSessionId,
          code,
        }),
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
      });

      if (!responseApi.ok) {
        const errorPayload = await responseApi.json().catch(() => null);
        console.error("[ACTIVATION-CONFIRM][STEP1] ERROR responseApi.ok=false", { status: responseApi.status, statusText: responseApi.statusText, errorPayload });
        const errorMessage = errorPayload?.message ?? errorPayload?.Message ?? errorPayload?.mensaje;
        setTenantGateStatus(tenantGateStatuses.MFA_REQUIRED);
        setTenantGateMessage(errorMessage ?? `Codigo no confirmado (${responseApi.status}).`);
        return;
      }

      const payload = await responseApi.json();
      const response = payload?.result ?? payload;
      const updatedActivation = {
        ...activation,
        mfaStatusCode: response?.statusCode ?? "PENDING_LICENSE",
      };

      saveStoredTenantActivation(updatedActivation);
      setTenantMfaSetup(updatedActivation);
      setTenantCorrelationId(response?.correlationId ?? "");
      setTenantGateStatus(tenantGateStatuses.MFA_CONFIRMED);
      setTenantGateMessage(response?.message ?? "MFA confirmado. Continúa con la licencia de activación.");
    } catch (error) {
      setTenantGateStatus(tenantGateStatuses.MFA_REQUIRED);
      setTenantGateMessage(`No fue posible confirmar el código: ${error?.message ?? "sin conexión"}`);
    }
  };

  const handleRecoverMobileTenant = async () => {
    if (!isMobileTenant || isRecoveringMobileTenant) return;
    const currentForm = normalizeTenantActivationForm(tenantActivationForm);
    const phoneNumber = String(currentForm.adminPhone.value || "").trim();
    if (!phoneNumber) {
      setTenantActivationForm({
        ...currentForm,
        adminPhone: { ...currentForm.adminPhone, error: true },
      });
      setTenantGateMessage("Captura el teléfono registrado para recuperar el acceso.");
      return;
    }

    setIsRecoveringMobileTenant(true);
    try {
      if (!mobileVerificationChallenge?.challengeId) {
        setTenantGateStatus(tenantGateStatuses.VALIDATING);
        setTenantGateMessage("Enviando código de verificación por WhatsApp...");
        const sendApi = await fetchTenantWithTimeout(apiTenantMobileChallengeSend, {
          method: "POST",
          body: JSON.stringify({
            licenseCode: currentForm.licenseGuid.value,
            phoneNumber,
            purpose: "MOBILE_RECOVERY",
          }),
          headers: { "Content-Type": "application/json", "Accept": "application/json" },
        }, 30000);
        const sendPayload = await sendApi.json().catch(() => null);
        if (!sendApi.ok || sendPayload?.hasError) throw new Error(sendPayload?.message || "No fue posible enviar el código.");
        const challenge = sendPayload?.result ?? sendPayload;
        setMobileVerificationChallenge(challenge);
        setTenantGateStatus(tenantGateStatuses.NEEDS_ACTIVATION);
        setTenantGateMessage(`Enviamos un código por WhatsApp al teléfono ${challenge.phoneMasked}. Captúralo para continuar.`);
        return;
      }

      const verificationCode = String(currentForm.mfaCode.value || "").replace(/\D/g, "");
      if (verificationCode.length !== 6) throw new Error("Captura el código de 6 dígitos enviado por WhatsApp.");
      const confirmApi = await fetchTenantWithTimeout(apiTenantMobileChallengeConfirm, {
        method: "POST",
        body: JSON.stringify({ challengeId: mobileVerificationChallenge.challengeId, code: verificationCode }),
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
      }, 30000);
      const confirmPayload = await confirmApi.json().catch(() => null);
      if (!confirmApi.ok || confirmPayload?.hasError) throw new Error(confirmPayload?.message || "Código no válido o vencido.");

      setTenantGateStatus(tenantGateStatuses.VALIDATING);
      setTenantGateMessage("Validando administrador y dispositivo...");
      const recoveryApi = await fetchTenantWithTimeout(apiTenantMobileDeviceRecover, {
        method: "POST",
        body: JSON.stringify({
          licenseCode: currentForm.licenseGuid.value,
          phoneNumber,
          deviceAlias: currentForm.deviceAlias.value,
          mfaCode: currentForm.mfaCode.value,
          verificationChallengeId: mobileVerificationChallenge.challengeId,
          appKey: "DASHBOARD",
          appVersion: process.env.REACT_APP_VERSION || "1.0.0",
        }),
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
      }, 30000);
      const recoveryPayload = await recoveryApi.json().catch(() => null);
      if (!recoveryApi.ok || recoveryPayload?.hasError) {
        throw new Error(recoveryPayload?.message || "No fue posible validar el dispositivo.");
      }

      const result = recoveryPayload?.result ?? recoveryPayload;
      const recoveredDevice = normalizeDeviceData({
        ...result,
        licenseCode: currentForm.licenseGuid.value,
        deviceAlias: currentForm.deviceAlias.value,
        fingerprint: "",
        fingerprintVersion: "MOBILE_BROWSER_V1",
        recoveredAt: new Date().toISOString(),
      });
      await saveStoredTenantDevice(recoveredDevice);
      setMobileVerificationChallenge(null);
      if (result.tenantAccessToken) localStorage.setItem(tenantAccessTokenName, result.tenantAccessToken);
      setTenantAdminStatus(result);
      setTenantGateStatus(result.canLogin ? tenantGateStatuses.AUTHORIZED : tenantGateStatuses.DENIED);
      setTenantGateMessage(result.canLogin ? "" : (result.message || "Acceso no disponible."));
      if (result.canLogin) navigate(routeLogin, { replace: true });
    } catch (error) {
      setTenantGateStatus(tenantGateStatuses.NEEDS_ACTIVATION);
      setTenantGateMessage(error?.message || "No fue posible recuperar el acceso.");
    } finally {
      setIsRecoveringMobileTenant(false);
    }
  };

  const handleRedeemTenantActivation = async () => {
    if (isRedeemingTenant) return;
    setIsRedeemingTenant(true);
    try {
      const activation = tenantMfaSetup ?? getStoredTenantActivation();
      const stored = await getStoredTenantDevice();
      const storedForLicense = isStoredDeviceForLicense(stored, activation?.licenseCode) ? stored : null;

      if (!activation?.activationSessionId || !activation?.licenseCode) {
        setTenantGateStatus(tenantGateStatuses.NEEDS_ACTIVATION);
        setTenantGateMessage("Inicia nuevamente la activación.");
        return;
      }

      const devicePublicId = String(activation.devicePublicId ?? storedForLicense?.devicePublicId ?? createSecureId()).trim().toUpperCase();
      const deviceKey = String(activation.deviceKey ?? storedForLicense?.deviceKey ?? createDeviceSecret()).trim().toUpperCase();

      if (isMobileTenant) {
        try {
          setTenantGateStatus(tenantGateStatuses.VALIDATING);
          setTenantGateMessage("Registrando dispositivo móvil...");
          const deviceAlias = String(activation.deviceAlias || activation.adminName || "Dispositivo portatil").trim();
          const responseApi = await fetchTenantWithTimeout(apiTenantMobileActivationRedeem, {
            method: "POST",
            body: JSON.stringify({
              activationSessionId: activation.activationSessionId,
              licenseCode: String(activation.licenseCode).trim().toUpperCase(),
              devicePublicId,
              deviceKey,
              fingerprint: "",
              fingerprintVersion: "MOBILE_BROWSER_V1",
              nombreEquipo: String(activation.adminPhone || "").replace(/\D/g, ""),
              plataforma: "MOBILE",
              appKey: "DASHBOARD",
              appVersion: process.env.REACT_APP_VERSION || "1.0.0",
            }),
            headers: { "Content-Type": "application/json", "Accept": "application/json" },
          }, 30000);
          const payload = await responseApi.json().catch(() => null);
          if (!responseApi.ok || payload?.hasError) {
            throw new Error(payload?.message || "No fue posible activar el dispositivo.");
          }

          const result = payload?.result ?? payload;
          const finalDevice = normalizeDeviceData({
            ...activation,
            ...result,
            tenantKey: result.tenantKey,
            devicePublicId: result.devicePublicId || devicePublicId,
            deviceKey,
            deviceAlias,
            fingerprint: "",
            fingerprintVersion: "MOBILE_BROWSER_V1",
            activatedAt: new Date().toISOString(),
          });
          await saveStoredTenantDevice(finalDevice);

          if (result.requiresProvisioning) {
            await fetchTenantWithTimeout(apiTenantProvisioningRun, {
              method: "POST",
              body: JSON.stringify({ tenantKey: finalDevice.tenantKey, devicePublicId, deviceKey, appKey: "DASHBOARD", appVersion: process.env.REACT_APP_VERSION || "1.0.0" }),
              headers: { "Content-Type": "application/json", "Accept": "application/json" },
            }, 120000);
          }

          const status = await validateTenantDevice(finalDevice, "");
          if (status === tenantGateStatuses.AUTHORIZED) {
            localStorage.removeItem(arsyncTenantActivationName);
            setTenantMfaSetup(null);
            navigate(routeLogin, { replace: true });
          }
          return;
        } catch (error) {
          setTenantGateStatus(tenantGateStatuses.MFA_CONFIRMED);
          setTenantGateMessage(error?.message || "No fue posible registrar el dispositivo móvil.");
          return;
        }
      }

      let nombreEquipo = (window.location.hostname ?? "").trim();
      let printerToolsData = null;
      try {
        printerToolsData = await getPrinterToolsFingerprint();
        if (printerToolsData?.machineName) {
          nombreEquipo = printerToolsData.machineName.trim();
        }
      } catch {}

      if (!printerToolsData?.fingerprint) {
        setTenantGateStatus(tenantGateStatuses.PRINTERTOOLS_REQUIRED);
        setTenantGateMessage("PrinterTools no entrego la huella del equipo. Abre PrinterTools y reintenta.");
        return;
      }

      try {
        setTenantGateStatus(tenantGateStatuses.VALIDATING);
        const responseApi = await fetch(apiTenantActivationRedeem, {
          method: "POST",
          body: JSON.stringify({
            activationSessionId: activation.activationSessionId,
            licenseCode: String(activation.licenseCode ?? "").trim().toUpperCase(),
            devicePublicId,
            deviceKey,
            fingerprint: printerToolsData.fingerprint,
            fingerprintVersion: "PRINTERTOOLS_V1",
            nombreEquipo,
            plataforma: navigator.platform || "WEB",
            appKey: "DASHBOARD",
            appVersion: process.env.REACT_APP_VERSION || "1.0.0",
          }),
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
          },
        });

        if (!responseApi.ok) {
          const errorPayload = await responseApi.json().catch(() => null);
          console.error("[ACTIVATION-REDEEM][PC] ERROR responseApi.ok=false", { status: responseApi.status, errorPayload });
          const errorMessage = errorPayload?.message ?? errorPayload?.Message ?? errorPayload?.mensaje;
          setTenantGateStatus(tenantGateStatuses.MFA_CONFIRMED);
          setTenantGateMessage(errorMessage ?? `No fue posible redimir la licencia (${responseApi.status}).`);
          return;
        }

        const payload = await responseApi.json();
        const response = payload?.result ?? payload;
        const finalDevice = {
          ...activation,
          ...storedForLicense,
          tenantKey: response?.tenantKey,
          tenantName: response?.tenantName,
          tenantStatus: response?.tenantStatus,
          licenseCode: activation.licenseCode,
          devicePublicId,
          deviceKey,
          activatedAt: new Date().toISOString(),
        };

        await saveStoredTenantDevice(finalDevice);
        localStorage.removeItem(arsyncTenantActivationName);
        setTenantCorrelationId(response?.correlationId ?? "");
        setTenantAdminStatus(response);

        if (response?.canLogin) {
          if (response?.tenantAccessToken) {
            localStorage.setItem(tenantAccessTokenName, response.tenantAccessToken);
            localStorage.removeItem(arsyncTenantActivationName);
            setTenantMfaSetup(null);
            setTenantGateStatus(tenantGateStatuses.AUTHORIZED);
            setTenantGateMessage("");
            navigate(routeLogin, { replace: true });
            return;
          }

          if (response?.requiresProvisioning) {
            setTenantGateStatus(tenantGateStatuses.PENDING);
            setTenantGateMessage(response?.message ?? "La base tenant sigue pendiente de provisionamiento. Revisa el estado para continuar.");
            return;
          }

          bootstrapPromiseRef.current = null;
          const tenantAuthorized = await handleTenantGateBootstrap();
          if (tenantAuthorized) {
            navigate(routeLogin, { replace: true });
          }
          return;
        }

        setTenantGateStatus(tenantGateStatuses.PENDING);
        setTenantGateMessage(response?.message ?? "Licencia activada. Revisa el estado del equipo.");
      } catch (error) {
        setTenantGateStatus(tenantGateStatuses.MFA_CONFIRMED);
        setTenantGateMessage(`No fue posible redimir la licencia: ${error?.message ?? "sin conexión"}`);
      }

    } finally {
      setIsRedeemingTenant(false);
    }
  };

  const handleRetryTenantGate = async () => {
    bootstrapPromiseRef.current = null;
    const tenantAuthorized = await handleTenantGateBootstrap();
    if (tenantAuthorized) {
      navigate(routeLogin, { replace: true });
    }
  };

  const handleReassignTenantDevice = async () => {
    const storedDevice = await getStoredTenantDevice();
    if (!storedDevice?.tenantKey || !storedDevice?.devicePublicId) {
      setTenantGateStatus(tenantGateStatuses.DENIED);
      setTenantGateMessage("No se encontraron datos del equipo. Realiza una nueva activación.");
      return;
    }

    setTenantGateStatus(tenantGateStatuses.VALIDATING);
    setTenantGateMessage("Reasignando equipo...");

    let nombreEquipo = (window.location.hostname ?? "").trim();
    let printerToolsData = null;
    try {
      printerToolsData = await getPrinterToolsFingerprint();
      if (printerToolsData?.machineName) {
        nombreEquipo = printerToolsData.machineName.trim();
      }
    } catch {}

    try {
      const responseApi = await fetch(apiTenantDeviceReassign, {
        method: "POST",
        body: JSON.stringify({
          tenantKey: String(storedDevice.tenantKey ?? "").trim().toUpperCase(),
          devicePublicId: String(storedDevice.devicePublicId ?? "").trim().toUpperCase(),
          fingerprint: String(printerToolsData?.fingerprint ?? "").trim(),
          fingerprintVersion: printerToolsData?.fingerprint ? "PRINTERTOOLS_V1" : "",
          nombreEquipo,
          plataforma: navigator.platform || "WEB",
          appKey: "DASHBOARD",
          appVersion: process.env.REACT_APP_VERSION || "1.0.0",
        }),
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
      });

      const payload = await responseApi.json();
      const response = payload?.result ?? payload;

      if (!responseApi.ok || payload?.hasError) {
        setTenantGateStatus(tenantGateStatuses.DENIED);
        setTenantGateMessage(response?.message ?? "No fue posible reasignar el equipo.");
        return;
      }

      if (response?.statusCode === "ACTIVE" && response?.deviceKey) {
        const updatedDevice = {
          ...storedDevice,
          deviceKey: response.deviceKey,
          devicePublicId: response.devicePublicId || storedDevice.devicePublicId,
        };
        await saveStoredTenantDevice(updatedDevice);
        localStorage.removeItem(arsyncTenantActivationName);
        setTenantMfaSetup(null);
        setTenantGateStatus(tenantGateStatuses.AUTHORIZED);
        setTenantGateMessage("");
        navigate(routeLogin, { replace: true });
        return;
      }

      setTenantGateStatus(tenantGateStatuses.DENIED);
      setTenantGateMessage(response?.message ?? "No fue posible reasignar el equipo.");
    } catch (error) {
      setTenantGateStatus(tenantGateStatuses.DENIED);
      setTenantGateMessage(`Error al reasignar: ${error?.message ?? "sin conexión"}`);
    }
  };

  const handleSubmitTenantAdminLogin = async ({ email, password, mfaCode }) => {
    const storedDevice = await getStoredTenantDevice();
    const device = storedDevice?.tenantKey && storedDevice?.devicePublicId && storedDevice?.deviceKey
      ? storedDevice
      : tenantAdminDevice;
    const adminEmail = (isNullOrEmpty(email) ? (device?.adminEmail ?? "") : email).trim();

    if (!device?.tenantKey || !device?.devicePublicId || !device?.deviceKey) {
      return {
        ok: false,
        message: "No se encontro la licencia local del equipo. Abre PrinterTools y reintenta.",
      };
    }

    try {
      const responseApi = await fetch(apiTenantAdminLogin, {
        method: "POST",
        body: JSON.stringify({
          tenantKey: String(device.tenantKey ?? "").trim().toUpperCase(),
          devicePublicId: String(device.devicePublicId ?? "").trim().toUpperCase(),
          deviceKey: String(device.deviceKey ?? "").trim().toUpperCase(),
          email: adminEmail,
          password,
          mfaCode,
          appKey: "DASHBOARD",
          appVersion: process.env.REACT_APP_VERSION || "1.0.0",
        }),
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
      });

      const payload = await responseApi.json().catch(() => null);
      const result = payload?.result ?? payload;

      if (!responseApi.ok || payload?.hasError) {
        return {
          ok: false,
          message: payload?.message ?? payload?.Message ?? result?.message ?? `Acceso no validado (${responseApi.status}).`,
        };
      }

      if (result?.requiresMfa) {
        return {
          ok: false,
          requiresMfa: true,
          message: result?.message ?? "Codigo MFA requerido.",
        };
      }

      const adminStatus = {
        ...result,
        canLogin: true,
        tenantKey: result?.tenantKey ?? device.tenantKey,
        devicePublicId: result?.devicePublicId ?? device.devicePublicId,
      };

      try {
        const databaseResponse = await fetch(apiTenantDeviceDatabaseName, {
          method: "POST",
          body: JSON.stringify({
            tenantKey: String(device.tenantKey ?? "").trim().toUpperCase(),
            devicePublicId: String(device.devicePublicId ?? "").trim().toUpperCase(),
            deviceKey: String(device.deviceKey ?? "").trim().toUpperCase(),
            appKey: "DASHBOARD",
            appVersion: process.env.REACT_APP_VERSION || "1.0.0",
          }),
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
          },
        });

        const databasePayload = await databaseResponse.json().catch(() => null);
        const databaseResult = databasePayload?.result ?? databasePayload;
        if (databaseResponse.ok && !databasePayload?.hasError && databaseResult?.databaseName) {
          adminStatus.databaseName = databaseResult.databaseName;
          adminStatus.provisionedDatabaseName = databaseResult.databaseName;
        }
      } catch {}

      setTenantAdminDevice(device);
      setTenantAdminStatus(adminStatus);
      setTenantCorrelationId(result?.correlationId ?? "");
      setTenantGateStatus(tenantGateStatuses.ADMIN_DASHBOARD);
      setTenantGateMessage(result?.message ?? "Acceso administrativo validado.");
      navigate("/administraciontenant", { replace: true });

      return {
        ok: true,
        message: result?.message ?? "Acceso administrativo validado.",
      };
    } catch (error) {
      return {
        ok: false,
        message: `No fue posible validar el acceso administrativo: ${error?.message ?? "sin conexión"}`,
      };
    }
  };

  const getTenantAdminLoginDefaults = async () => {
    const device = await getStoredTenantDevice();
    return {
      email: device?.adminEmail ?? "",
      tenantKey: device?.tenantKey ?? "",
      devicePublicId: device?.devicePublicId ?? "",
      deviceKey: device?.deviceKey ?? "",
      hasLocalDevice: !!(device?.tenantKey && device?.devicePublicId && device?.deviceKey),
    };
  };
  const handleClearTenantDeviceData = () => {
    localStorage.removeItem(arsyncTenantDeviceName);
    localStorage.removeItem(arsyncTenantActivationName);
    localStorage.removeItem(tenantAccessTokenName);
    if (!isMobileTenant) fetch(apiTenantLocalDeviceLicense, { method: "DELETE" }).catch(() => {});
    setTenantAdminStatus(null);
    setTenantAdminDevice(null);
    setTenantCorrelationId("");
  };

  const handleResetTenantActivation = () => {
    bootstrapPromiseRef.current = null;
    localStorage.removeItem(arsyncTenantDeviceName);
    localStorage.removeItem(arsyncTenantActivationName);
    localStorage.removeItem(tenantAccessTokenName);
    if (!isMobileTenant) fetch(apiTenantLocalDeviceLicense, { method: "DELETE" }).catch(() => {});
    setTenantActivationForm(getTenantActivationForm());
    setTenantMfaSetup(null);
    setTenantAdminStatus(null);
    setTenantAdminDevice(null);
    setTenantCorrelationId("");
    setTenantGateStatus(tenantGateStatuses.NEEDS_ACTIVATION);
    setTenantGateMessage("Activación requerida");
  };

  const handleDownloadPrinterTools = () => {
    GetFileRequest({
      url: apiTenantPrinterToolsDownload,
      filename: "PrinterToolsSizeSoftXSetup_v8.exe",
      loader: "Preparando descarga de PrinterTools...",
    });
  };

  const saveLoginResponse = (loginResponse, navigateAfterSave = true) => {
    saveSession({
      accessToken: loginResponse.result?.Token,
      refreshToken: loginResponse.result?.RefreshToken,
      tenantAccessToken: loginResponse.result?.TenantAccessToken
        || loginResponse.result?.sTenantAccessToken
        || loginResponse.result?.tenantAccessToken,
    });

    const datauser = {
      Usuario: loginResponse.result?.Usuario ? loginResponse.result?.Usuario : "",
      Nombre: loginResponse.result?.Nombre ? loginResponse.result?.Nombre : "",
      id: loginResponse.result?.idUser ? loginResponse.result?.idUser : 0,
    }

    setUsuarioData(datauser);
    setUsuario(loginResponse.result.Usuario);
    setToken(loginResponse.result?.Token);
    setTokenRefresh(loginResponse.result?.RefreshToken);
    setUsuarioEnSesion(true);

    if (!navigateAfterSave) return;

    // Login real (no refresh silencioso): rearma inactividad + preventivo tras un logout
    // que los desarmó. El tráfico automático nunca extiende inactividad: los saves con
    // navigateAfterSave=false no tocan timers. addEventListener idéntico no duplica.
    events.forEach((item) => {
      window.addEventListener(item, handleSesionActivity);
    });
    window.addEventListener("storage", handleCrossTabStorage);
    handleLogoutTimer();
    startPreventiveRefresh();

    if (previousRoute === routeLogin) {
      navigate(routeRoot, { replace: true });
      return;
    }

    if (!isNullOrEmpty(previousRoute)) {
      navigate(previousRoute, { replace: true });
    } else {
      navigate(routeRoot, { replace: true });
    }
  };

  // Única función de refresh de sesión (preventivo + arranque + validación manual;
  // Fase 3 la reutilizará para recuperación ante 401). Llamada HTTP directa con fetch:
  // no pasa por RequestContext para evitar recursión refresh->401->refresh.
  // Single-flight: mientras un refresh está en curso, los demás llaman comparten la
  // misma promesa (refreshPromiseRef persiste entre renders). Siempre limpia la
  // referencia al terminar, con éxito o fallo.
  const refreshSession = ({ navigateAfterSave = false, mostrarLoader = false } = {}) => {
    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }

    const { accessToken, refreshToken } = getSession();
    if (isNullOrEmpty(accessToken) || isNullOrEmpty(refreshToken)) {
      return Promise.resolve({ ok: false });
    }

    refreshPromiseRef.current = (async () => {
      try {
        if (mostrarLoader) handleOpenLoader("");
        const loginResponse = await fetch(apiLoginRefresh, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + getAuthorizationToken(token),
          },
          body: JSON.stringify({
            sToken: accessToken,
            sRefreshToken: refreshToken,
          }),
        });
        if (mostrarLoader) handleCloseLoader();

        if (loginResponse.ok) {
          const response = await loginResponse.json();
          if (!response.hasError) {
            saveLoginResponse(response, navigateAfterSave);
            return { ok: true, response };
          }
          registerRequestErrorLog({
            httpCode: 200,
            method: "POST",
            url: apiLoginRefresh,
            message: response.message || "El servicio rechazo el refresco de sesion.",
            source: "SesionContext.refresh",
            severity: "warning",
          });
          return { ok: false, httpCode: 200 };
        }

        registerRequestErrorLog({
          httpCode: loginResponse.status,
          method: "POST",
          url: apiLoginRefresh,
          message: "No fue posible refrescar la sesion.",
          source: "SesionContext.refresh",
          severity: "warning",
        });
        return { ok: false, httpCode: loginResponse.status };
      } catch {
        if (mostrarLoader) handleCloseLoader();
        registerRequestErrorLog({
          httpCode: 500,
          method: "POST",
          url: apiLoginRefresh,
          message: "Error inesperado al refrescar sesion.",
          source: "SesionContext.refresh",
          severity: "warning",
        });
        return { ok: false };
      } finally {
        refreshPromiseRef.current = null;
      }
    })();

    return refreshPromiseRef.current;
  };

  // const cargarDefinicionMenu = async(menumodal, tipouser) => {   
  const cargarDefinicionMenu = (menumodal, tipouser) => {
    const rows = Array.isArray(menumodal)
      ? menumodal
      : (menumodal?.menumodel ?? menumodal?.MenuModel ?? []);

    const definicionMenus = {
      _paths: [],
      _paginas: [], // si no lo usas, lo puedes quitar
    };

    const T = (s) => (s ?? "").toString().trim();
    const N = (v) => {
      const parsed = Number(v);
      if (!Number.isFinite(parsed) || parsed < 0) return 0;
      return Math.trunc(parsed);
    };

    for (const menu of rows) {
      const Menu = T(menu.menu ?? menu.Menu);
      const Pagina = T(menu.pagina ?? menu.Pagina);
      const Path = T(menu.path ?? menu.Path);
      const Mostrar = !!(menu.mostrar ?? menu.Mostrar);
      const tipo = T(menu.tipousuario ?? menu.TipoUsuario);
      const hotkey = T(menu.hotkey ?? menu.HotKey);
      const orden = N(menu.orden ?? menu.Orden);
      const ordenGrupo = N(menu.ordenGrupo ?? menu.ordengrupo ?? menu.OrdenGrupo ?? 0);

      if (!Menu || !Pagina) continue;

      if (!definicionMenus[Menu]) {
        definicionMenus[Menu] = {
          nombre: Menu,
          tipousuario: tipo,
          mostrar: false,
          orden: ordenGrupo != null && ordenGrupo > 0 ? ordenGrupo : (orden || Number.MAX_SAFE_INTEGER),
          _paginas: {},
        };
      }
      else if (ordenGrupo > 0) {
        definicionMenus[Menu].orden = Math.min(definicionMenus[Menu].orden ?? Number.MAX_SAFE_INTEGER, ordenGrupo);
      }
      else if (orden > 0) {
        definicionMenus[Menu].orden = Math.min(definicionMenus[Menu].orden ?? Number.MAX_SAFE_INTEGER, orden);
      }

      // tu lógica de status (la dejé equivalente pero más legible)
      const bloqueado = ["SA", "ADMINISTRADOR", "SUPERVISOR"].includes(tipo) ? false : (tipo !== tipouser);

      definicionMenus[Menu]._paginas[Pagina] = {
        nombre: Pagina,
        path: Path,
        mostrar: Mostrar,
        tipousuario: tipo,
        status: bloqueado,
        hotkey,
        orden,
      };

      definicionMenus._paths.push({ [Menu]: Path });
    }

    // 🔥 PADRE = OR de sus páginas
    Object.keys(definicionMenus).forEach((k) => {
      if (k.startsWith("_")) return;
      const paginas = Object.values(definicionMenus[k]._paginas ?? {});
      definicionMenus[k]._paginas = fromEntries(
        Object.entries(definicionMenus[k]._paginas ?? {}).sort(([, a], [, b]) => {
          const orderA = a.orden || Number.MAX_SAFE_INTEGER;
          const orderB = b.orden || Number.MAX_SAFE_INTEGER;
          if (orderA !== orderB) return orderA - orderB;
          return a.nombre.localeCompare(b.nombre);
        })
      );
      definicionMenus[k].mostrar = paginas.some((p) => !!p.mostrar);
    });

    const definicionOrdenada = {
      _paths: definicionMenus._paths,
      _paginas: definicionMenus._paginas,
    };

    Object.entries(definicionMenus)
      .filter(([k]) => !k.startsWith("_"))
      .sort(([, a], [, b]) => {
        const orderA = a.orden || Number.MAX_SAFE_INTEGER;
        const orderB = b.orden || Number.MAX_SAFE_INTEGER;
        if (orderA !== orderB) return orderA - orderB;
        return a.nombre.localeCompare(b.nombre);
      })
      .forEach(([k, value]) => {
        definicionOrdenada[k] = value;
      });

    setDefinicionMenu(definicionOrdenada);
  };

  const handleRecargarMenuSesion = async (idUser = usuarioData?.id, tipouser = tipoUsuario) => {
    if (!idUser) return false;

    const url = apiPostMenuModelUser.replace("{idUser}", idUser);
    const responseMenuModel = await fetch(url, {
      method: "get",
      headers: {
        "content-type": "application/json",
        Authorization: "Bearer " + getAuthorizationToken(token),
      },
    });

    const menuModel = await responseMenuModel.json();
    if (menuModel.hasError) {
      handleOpenAlert(menuModel.message, "error");
      return false;
    }

    await cargarDefinicionMenu(menuModel.result, tipouser);
    setListPermisosObjects(menuModel.result?.permisos ?? menuModel.result?.Permisos ?? [getPermisosHerramientas()]);
    return true;
  };

  const handleChangeLoginForm = (e) => {
    setLoginForm({
      ...loginForm,
      [e.target.name]: {
        value: e.target.value,
        error: false,
      },
    });
  };

  const handleSubmitLoginForm = async (e) => {
    e.preventDefault();
    const { password, username } = loginForm;

    const form = {
      ...loginForm,
      password: { ...password, error: password.value === "" },
      username: { ...username, error: username.value === "" },
    };

    setLoginForm(form);
    if (isErrorForm(form)) {
      return;
    }

    const loginRequest = {
      sUser: username.value,
      sPassword: password.value,
    };

    try {
      const tenantAccessToken = localStorage.getItem(tenantAccessTokenName);
      const loginResponse = await fetch(apiLogin, {
        method: "POST",
        body: JSON.stringify(loginRequest),
        headers: {
          'Authorization': `Bearer ${tenantAccessToken || tokenSeguridad}`,
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
        loader: "Validando acceso al sistema"
      });
      const response = await loginResponse.json();

      if (response.hasError) {
        handleOpenAlert("error en credenciales de acceso");
        return;
      }

      saveLoginResponse(response);

      let tipouser = response?.result.tipousuario;
      setTipoUsuario(tipouser);

      //Carga de datos de menumodel
      // Rev 9 (23/09/2026): el login rota el tenant JWE en el backend, por lo
      // que el menú debe pedirse con el JWE FRESCO recién guardado (no con el
      // capturado antes del submit, que puede estar caducado tras expirar la
      // sesión; eso dejaba el login atorado hasta recargar la página).
      const tenantAccessTokenFresco =
        localStorage.getItem(tenantAccessTokenName) || tenantAccessToken;
      const iduser = response.result?.idUser;
      const url = apiPostMenuModelUser.replace("{idUser}", iduser);
      const responseMenuModel = await fetch(url, {
        method: "get",
        headers: {
          "content-type": "application/json",
          Authorization: "Bearer " + tenantAccessTokenFresco,
        },
      });

      const menuModel = await responseMenuModel.json();
      if (menuModel.hasError) {
        handleOpenAlert(menuModel.message, "error");
        handleOpenLoader(false);
        return;
      }

      await cargarDefinicionMenu(menuModel.result, tipouser);

      //Lista de permisos a objetos del sistema
      await setListPermisosObjects(menuModel.result?.permisos ?? menuModel.result?.Permisos ?? [getPermisosHerramientas()]);

    } catch (error) {

      handleOpenAlert("Ocurrió un error inesperado al intentar validar las credenciales: " + error, "error");
    } finally {
      handleCloseLoader();
    }
  };

  const getPermisoObject = async (object) => {
    var result = {
      visible: false,
      enabled: false
    };

    if (!listPermisosObjects.lenght > 0) {
      result.visible = false;
      result.enabled = false;
      return result;
    }

    var filterResult = await listPermisosObjects.filter(e => e.Menu === object || e.Path === object)
    if (filterResult.Menu.leght > 0) {
      result.visible = true;
      result.enabled = filterResult.Staus;
      return result;
    }
  };

  // Único punto de cierre de sesión de usuario. Con guard: un solo logout efectivo
  // aunque inactividad, refresh fallido y 401 concurran. Desarma timers/listeners para
  // que un timer viejo no cierre una sesión iniciada después. Conserva Tenant/JWE y
  // arsync-* (activación/licencia/dispositivo intactos).
  // El cierre termina con RECARGA COMPLETA a /login (no navigate SPA): el bootstrap del
  // gate vuelve a validar el dispositivo y renueva el JWE (misma vigencia 30min que el
  // JWT), por lo que el siguiente login autentica sin necesidad de refresh manual.
  const handleClickCerrarSesion = () => {
    if (isLoggingOutRef.current) return;
    isLoggingOutRef.current = true;
    try {
      clearLogoutTimer();
      clearRefreshTimer();
      events.forEach((item) => {
        window.removeEventListener(item, handleSesionActivity);
      });
      window.removeEventListener("storage", handleCrossTabStorage);
      setToken("");
      setTokenRefresh("");
      setPermisosModulos([]);
      setConfiguracion(getConfiguracionDefault());
      setUsuarioEnSesion(false);
      setLoginForm(getLoginForm());
      clearUserSession();
      setPreviousRoute(location.pathname);
      setUsuario(null);
      setDefinicionMenu(getDefinicionMenu());
      // Recarga como primera entrada: reejecuta bootstrap + gate y deja el canal
      // de comunicaciones (JWE/JWT) listo para el siguiente login.
      window.location.replace(routeLogin);
    } finally {
      isLoggingOutRef.current = false;
    }
  };

  // Preventivo 15min: renueva JWT/JWE antes de su expiración técnica (30min).
  // Solo corre con sesión válida (refreshSession sale sin HTTP si no hay tokens).
  const startPreventiveRefresh = () => {
    if (refreshTimerRef.current) return;
    refreshTimerRef.current = setInterval(() => {
      refreshSession({ navigateAfterSave: false, mostrarLoader: false });
    }, preventiveRefreshMs);
  };

  const handleClickRegresarLogin = async () => {
    navigate(routeLogin);
  };

  const handleClickAcercaDe = async () => {
    navigate(routeAcercaDe);
  };

  const handleChangeCambiarPasswordForm = (e) => {
    setCambiarPasswordForm({
      ...cambiarPasswordForm,
      [e.target.name]: {
        value: e.target.value,
        error: false,
      },
    });
  };

  const handleSubmitCambiarPasword = async () => {
    const { username } = cambiarPasswordForm;

    const form = {
      ...cambiarPasswordForm,
      username: {
        ...username,
        error: isNullOrEmpty(username.value) || !rxCaracteresEsp.test(username.value),
      },
    };

    setCambiarPasswordForm(form);

    if (isErrorForm(form)) {
      return;
    }

    const formSubmit = {
      sUser: username.value,
    };

    try {
      handleOpenLoader("");
      const responseApi = await fetch(apiCambiarPasswordInicial, {
        method: "POST",
        body: JSON.stringify(formSubmit),
        headers: {
          "content-type": "application/json",
          Authorization: "Bearer " + getAuthorizationToken(token),
        },
      });

      handleCloseLoader();

      const response = await responseApi.json();

      if (response.hasError) {
        handleOpenAlert(response.message, "error");
        return;
      }

      handleOpenAlert(response.message, "success");
      handleCloseCambiarPassworForm();

    } catch (error) {
      handleCloseLoader();
      handleOpenAlert("Ocurrió un error inesperado al intentar cambiar la contraseña", "error");
    }
  };

  const handleCloseCambiarPassworForm = () => {
    setModalOpenCambioPasswordForm(false);
    setCambiarPasswordForm(getCambiarPasswordForm());
  };

  const handleClickCambiarPassword = () => {
    setModalOpenCambioPasswordForm(true);
  };

  const handleClicValidarActivarSesion = async () => {
    const { accessToken, refreshToken } = getSession();

    if (isNullOrEmpty(accessToken) || isNullOrEmpty(refreshToken)) {
      setUsuarioEnSesion(false);
      return;
    }

    // Misma semántica que antes: éxito guarda y navega; cualquier fallo cierra sesión.
    const result = await refreshSession({ navigateAfterSave: true, mostrarLoader: true });
    if (!result.ok) {
      handleClickCerrarSesion();
    }
  };

  // Expone a RequestContext (vía puente neutro, sin dependencia circular) la MISMA
  // refreshSession single-flight de Fase 2 y el logout de SesionContext, para el
  // retry-401. Sin deps: se re-registra cada render, siempre sin closures rancios.
  useEffect(() => {
    registerSessionHandlers({ refresh: refreshSession, logout: handleClickCerrarSesion });
  });

  const getPermisoBoton = (ruta, nombreBoton) => {
    const permisodata = listPermisosObjects.find(
      (item) => item.Menu.trim().toUpperCase() === nombreBoton.trim().toUpperCase() && item.Path === ruta
    );
    return !isNullOrEmpty(permisodata) ? item.Status : false;
  };

  const getVisualizacionBoton = (nombreBoton) => {
    const botondata = listPermisosObjects?.find(
      (item) => item.Menu?.trim().toUpperCase() === nombreBoton.trim().toUpperCase()
    );
    return isNullOrEmpty(botondata) ? true : false; //False = mostrar True = ocultar
  };

  const getNombreBoton = (ruta, nombreBoton) => {
    const item = permisosBotones.find(
      (item) => item.Nombre.toUpperCase() === nombreBoton.toUpperCase() && item.Ruta === ruta
    );
    return !isNullOrEmpty(item) ? item.Nombre : "";
  };

  const getPermisoPagina = (nombrePagina) => {
    const item = permisosPaginas.find(
      (item) => item.Nombre.toUpperCase() === nombrePagina.toUpperCase()
    );
    return !isNullOrEmpty(item) ? item.Estado : 0;
  };

  const getNombrePagina = (nombrePagina) => {
    const item = permisosPaginas.find(
      (item) => item.Nombre.toUpperCase() === nombrePagina.toUpperCase()
    );
    return !isNullOrEmpty(item) ? item.Nombre : "";
  };

  const getPermisoModulo = (nombreModulo) => {
    const item = permisosModulos.find(
      (item) => item.Nombre.toUpperCase() === nombreModulo.toUpperCase()
    );
    return !isNullOrEmpty(item) ? item.Estado : 0;
  };

  const getNombreModulo = (nombreModulo) => {
    const item = permisosModulos.find(
      (item) => item.Nombre.toUpperCase() === nombreModulo.toUpperCase()
    );
    return !isNullOrEmpty(item) ? item.Nombre : "";
  };

  const handleObtenerParametrosCaja = async () => {

    const tipoventaanterior = parametrosCajaForm.tipoventa;
    let idPC = localStorage.getItem("IDPC");
    if (idPC === null || idPC.length === 0 || idPC === undefined) {
      let idPC2 = newId();
      localStorage.setItem("IDPC", idPC2);
      setPcData({ value: idPC2, error: "" });
      idPC = idPC2;
    }
    else {
      setPcData({ value: idPC, error: "" });
    }

    let idnewvalidato = await handlePCValidator(idPC);

    let pcname = "";

    let url = apiParametrosCaja.replace("{idCaja}", idnewvalidato ?? idPC);
    const response = await GetRequest({
      url,
      loader: "Consultando parámetros de caja...",
    });

    if (response.hasError) {
      setParametrosCajaForm(getParametrosCajaForm());
      return;
    }

    let idx = idnewvalidato ?? idPC;

    let caja = pcname ? pcname : response?.result.caja.trim() ? response?.result.caja.trim() : "CAJA-DEFAULT";
    setPcData({
      ...pcData,
      namepc: { value: caja, error: "" },
      idpc: { value: idx, error: "" }
    });

    if (response.result == null) {
      setParametrosCajaForm(getParametrosCajaForm());
      return;
    }
    else {
      const newParams = {
        ...response?.result,
        tipoventa: tipoventaanterior,
        // No heredar valores antiguos que pudieran venir de ParametrosCaja.
        // La única fuente válida es TICKET_LAYOUT_{caja} en Parametros.
        ticketLayout: {},
        ticketLayoutRegistrado: false,
      };
      // Cargar props de ticket para esta caja (tabla Parametros)
      try {
        const nombreLayout = `TICKET_LAYOUT_${idx}`;
        const respProps = await GetRequest({
          url: apiParametrosGet.replace("{nombre}", nombreLayout),
          loader: "Consultando propiedades de ticket...",
        });

        if (!respProps.hasError && respProps.result != null) {
          const parsed = typeof respProps.result === "string" ? JSON.parse(respProps.result) : respProps.result;

          if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
            newParams.ticketLayout = parsed;
            newParams.ticketLayoutRegistrado = true;
          }
        }
      } catch (e) {
        newParams.ticketLayout = {};
        newParams.ticketLayoutRegistrado = false;
      }
      setParametrosCajaForm(newParams);
      if (newParams.nombreimpresoraetiqueta) {
        localStorage.setItem('nombreimpresoraetiqueta', newParams.nombreimpresoraetiqueta.trim());
      }
    }

    return parametrosCajaForm;
  };

  const handleObtenerParametrosCajaSinValidacion = async (parametros) => {
    const tipoventaanterior = parametros?.tipoventa;
    let idPC = parametros?.idpc || parametros?.idPC || localStorage.getItem("IDPC") || "";

    if (idPC === null || idPC.length === 0 || idPC === undefined) {
      return;
    }

    let url = apiParametrosCaja.replace("{idCaja}", idPC);
    const response = await GetRequest({
      url,
      loader: "Consultando parámetros de caja...",
    });

    if (response.hasError) {
      return getParametrosCajaForm();
    }

    const resultData = {
      ...response?.result,
      tipoventa: tipoventaanterior || response?.result?.tipoventa,
      // ParametrosCaja no activa el diseñador aunque traiga datos heredados.
      ticketLayout: {},
      ticketLayoutRegistrado: false,
    };
    // Cargar props de ticket para esta caja (tabla Parametros)
    try {
      const nombreLayout = `TICKET_LAYOUT_${idPC}`;
      const respProps = await GetRequest({
        url: apiParametrosGet.replace("{nombre}", nombreLayout),
        loader: "Consultando propiedades de ticket...",
      });

      if (!respProps.hasError && respProps.result != null) {
        const parsed = typeof respProps.result === "string" ? JSON.parse(respProps.result) : respProps.result;
        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
          resultData.ticketLayout = parsed;
          resultData.ticketLayoutRegistrado = true;
        }
      }
    } catch (e) {
      resultData.ticketLayout = {};
      resultData.ticketLayoutRegistrado = false;
    }
    setParametrosCajaForm(resultData);
    if (resultData.nombreimpresoraetiqueta) {
      localStorage.setItem('nombreimpresoraetiqueta', resultData.nombreimpresoraetiqueta.trim());
    }
    return resultData;
  };

  const handlePCValidator = async (id) => {
    let pcName = "";

    try {
      const responsepcdata = await fetchTenantWithTimeout(`http://localhost:4000/api/nombreequipo`, {}, 4000);

      if (!responsepcdata.ok) {
        throw new Error(`No está instalado PrinterTools en el equipo: ${responsepcdata.statusText}`);
      }

      const data = await responsepcdata.json();
      pcName = data.name ?? "";
    } catch (error) {
      pcName = "";
    }

    if (isNullOrEmpty(id) || isNullOrEmpty(pcName)) {
      return;
    }

    let urlvalidator = apiCompuValidator.replace("{id}", id).replace("{pc}", pcName.trim());
    let responsevalidator = null;

    try {
      responsevalidator = await PostRequest({
        url: urlvalidator,
        body: {},
        loader: "Validando estación de trabajo...",
        timeout: 8000,
      });
    } catch {
      return;
    }

    if (responsevalidator?.hasError) {
      handleOpenAlert("Error al fijar nombre de la PC " + (responsevalidator.error || responsevalidator.message || "") + " " + pcName.trim());
      return;
    }

    if (isNullOrEmpty(responsevalidator?.result)) { return; }

    setParametrosCajaForm({
      ...parametrosCajaForm,
      caja: responsevalidator.result.pc.trim().toUpperCase(),
      idpc: responsevalidator.result.id.trim(),
    });

    setPcData({
      ...pcData,
      namepc: { value: responsevalidator.result.pc.trim().toUpperCase(), error: "" },
    });

    localStorage.setItem("IDPC", responsevalidator.result.id.trim());
    return responsevalidator.result.id.trim();
  };

  // =====================================================================
  // Rev.12 LIC. José Luis Aragón C. | Multi-tenant aditivo (no modifica lógica existente).
  // - PrinterTools expone fingerprint/machineName del hardware (mismos datos
  //   para todas las licencias) pero solo guarda UNA licencia.
  // - Los perfiles secundarios viven en arsync-tenant-profiles (navegador).
  // - switch/request validan DIRECTO a Seguridad con el mismo fingerprint,
  //   sin POST a localhost device-license para no borrar la primaria.
  // =====================================================================
  const refreshTenantProfiles = () => {
    try {
      const next = listStoredTenantProfiles();
      setTenantProfiles(next);
      return next;
    } catch {
      return tenantProfiles;
    }
  };

  // Fuente de verdad: backend Seguridad lista licencias/BDs por hardware.
  // Frontend solo envía fingerprint + nombreEquipo (mismos datos PrinterTools).
  const fetchTenantProfilesFromBackend = async () => {
    try {
      const shared = await getSharedPrinterFingerprint().catch(() => null);
      const fingerprint = String(shared?.fingerprint ?? "").trim();
      const nombreEquipo = String(shared?.machineName ?? "").trim();
      if (!fingerprint && !nombreEquipo) return refreshTenantProfiles();
      const responseApi = await fetchTenantWithTimeout(apiTenantDeviceListByHardware, {
        method: "POST",
        body: JSON.stringify({
          fingerprint,
          nombreEquipo,
          fingerprintVersion: fingerprint ? (isMobileTenant ? "MOBILE_BROWSER_V1" : "PRINTERTOOLS_V1") : "",
          plataforma: navigator.platform || navigator.userAgent || "",
          appKey: "DASHBOARD",
          appVersion: process.env.REACT_APP_VERSION || "1.0.0",
          includePending: true,
        }),
        headers: { "Content-Type": "application/json", Accept: "application/json" },
      }, 12000);
      const { payload } = await readTenantDebugResponse(responseApi);
      if (!responseApi.ok || payload?.hasError) return refreshTenantProfiles();
      const items = payload?.result?.items ?? payload?.items ?? [];
      if (!Array.isArray(items) || items.length === 0) return refreshTenantProfiles();
      const cachedAll = listStoredTenantProfiles();
      items.forEach((it) => {
        const tenantKey = String(it.tenantKey ?? "").toUpperCase();
        if (!tenantKey) return;
        const cached = cachedAll.find((p) => String(p.tenantKey).toUpperCase() === tenantKey);
        const devicePublicId = String(cached?.devicePublicId || it.devicePublicId || "").toUpperCase();
        const deviceKey = String(cached?.deviceKey || "").toUpperCase();
        if (!devicePublicId || !deviceKey) return;
        persistTenantProfile({
          tenantKey,
          licenseCode: String(it.licenseCode || cached?.licenseCode || tenantKey).toUpperCase(),
          devicePublicId,
          deviceKey,
          tenantName: String(it.tenantName || cached?.tenantName || ""),
          databaseName: String(it.databaseName || cached?.databaseName || ""),
          deviceAlias: String(cached?.deviceAlias || nombreEquipo || ""),
          fingerprint,
          machineName: nombreEquipo,
          adminEmail: String(cached?.adminEmail || ""),
          adminName: String(cached?.adminName || ""),
          status: it.isPendingApproval ? "PENDING_APPROVAL" : (cached?.status || it.deviceStatus || "AUTHORIZED"),
          requestId: it.requestId ?? cached?.requestId,
          lastUsedAt: cached?.lastUsedAt || new Date().toISOString(),
        });
      });
      const finalList = listStoredTenantProfiles();
      setTenantProfiles(finalList);
      return finalList;
    } catch {
      return refreshTenantProfiles();
    }
  };

  const getSharedPrinterFingerprint = async () => {
    try {
      const pt = await getPrinterToolsFingerprint();
      if (pt?.fingerprint) return pt;
    } catch {}
    return {
      fingerprint: buildTenantFingerprint(),
      machineName: (window.location.hostname || "").trim(),
    };
  };

  const syncActiveDeviceToProfiles = async () => {
    try {
      const active = getBrowserStoredTenantDevice() || tenantAdminDevice;
      if (!active?.tenantKey || !active?.devicePublicId || !active?.deviceKey) return refreshTenantProfiles();
      const shared = await getSharedPrinterFingerprint().catch(() => null);
      const next = persistTenantProfile({
        tenantKey: String(active.tenantKey ?? active.licenseCode ?? "").toUpperCase(),
        licenseCode: String(active.licenseCode ?? active.tenantKey ?? "").toUpperCase(),
        devicePublicId: String(active.devicePublicId ?? "").toUpperCase(),
        deviceKey: String(active.deviceKey ?? "").toUpperCase(),
        tenantName: String(active.tenantName ?? tenantAdminStatus?.tenantName ?? "").trim(),
        databaseName: String(active.databaseName ?? tenantAdminStatus?.databaseName ?? tenantAdminStatus?.provisionedDatabaseName ?? "").trim(),
        deviceAlias: String(active.deviceAlias ?? active.nombreEquipo ?? "").trim(),
        fingerprint: String(shared?.fingerprint ?? active.fingerprint ?? "").trim(),
        machineName: String(shared?.machineName ?? active.machineName ?? "").trim(),
        adminEmail: String(active.adminEmail ?? "").trim(),
        adminName: String(active.adminName ?? "").trim(),
        status: tenantGateStatus === tenantGateStatuses.AUTHORIZED ? "AUTHORIZED" : (active.status || "AUTHORIZED"),
        lastUsedAt: new Date().toISOString(),
      });
      setTenantProfiles(next);
      return next;
    } catch {
      return tenantProfiles;
    }
  };

  const switchTenantProfile = async (tenantKey) => {
    const target = findStoredTenantProfile(tenantKey);
    if (!target) return { ok: false, message: "Perfil no encontrado en este equipo." };
    try {
      const shared = await getSharedPrinterFingerprint();
      const machineName = String(shared?.machineName || target.machineName || window.location.hostname || "").trim();
      const fingerprint = String(shared?.fingerprint || target.fingerprint || "").trim();
      const validateUrl = isMobileTenant ? apiTenantMobileDeviceValidate : apiTenantDeviceValidate;
      const responseApi = await fetchTenantWithTimeout(validateUrl, {
        method: "POST",
        body: JSON.stringify({
          tenantKey: String(target.tenantKey ?? "").toUpperCase(),
          devicePublicId: String(target.devicePublicId ?? "").toUpperCase(),
          deviceKey: String(target.deviceKey ?? "").toUpperCase(),
          fingerprint,
          nombreEquipo: machineName,
          appKey: "DASHBOARD",
          appVersion: process.env.REACT_APP_VERSION || "1.0.0",
        }),
        headers: { "Content-Type": "application/json", Accept: "application/json" },
      }, 12000);
      const { payload } = await readTenantDebugResponse(responseApi);
      if (!responseApi.ok || payload?.hasError) {
        return { ok: false, message: payload?.message ?? `No fue posible validar la empresa (${responseApi.status}).` };
      }
      const result = payload?.result ?? payload;
      const gate = mapTenantGateResponse(result);
      if (gate.status !== tenantGateStatuses.AUTHORIZED) {
        const next = persistTenantProfile({ ...target, status: gate.status, lastUsedAt: new Date().toISOString() });
        setTenantProfiles(next);
        setTenantGateMessage(gate.message);
        return { ok: false, message: gate.message || "La empresa seleccionada aún no autoriza este equipo.", status: gate.status };
      }
      // Activa el perfil: mismo mecanismo que el login normal (JWE + device activo),
      // sin tocar PrinterTools para no borrar la licencia primaria.
      const activated = normalizeDeviceData({
        ...target,
        tenantKey: String(result?.tenantKey ?? target.tenantKey ?? "").toUpperCase(),
        tenantName: String(result?.tenantName ?? target.tenantName ?? ""),
        databaseName: String(result?.databaseName ?? result?.provisionedDatabaseName ?? target.databaseName ?? ""),
        fingerprint,
        machineName,
        status: "AUTHORIZED",
        lastUsedAt: new Date().toISOString(),
      });
      localStorage.setItem(arsyncTenantDeviceName, JSON.stringify(activated));
      if (result?.tenantAccessToken) localStorage.setItem(tenantAccessTokenName, result.tenantAccessToken);
      setTenantAdminStatus(result);
      setTenantAdminDevice(activated);
      setTenantGateStatus(tenantGateStatuses.AUTHORIZED);
      setTenantGateMessage("");
      const next = persistTenantProfile({ ...activated, tenantKey: activated.tenantKey });
      setTenantProfiles(next);
      return { ok: true, profile: activated, result };
    } catch (error) {
      return { ok: false, message: error?.message ?? "Sin conexión para cambiar de empresa." };
    }
  };

  const requestAdditionalTenantAccess = async ({ licenseGuid, adminName, adminEmail, deviceAlias }) => {
    const formatted = formatTenantActivationGuid(licenseGuid ?? "");
    if (!tenantActivationGuidPattern.test(formatted)) {
      return { ok: false, message: "Captura una licencia válida ARXS-XXXX-XXXX-XXXX." };
    }
    if (isNullOrEmpty(adminEmail) || !String(adminEmail).includes("@")) {
      return { ok: false, message: "Captura el correo del administrador." };
    }
    if (isNullOrEmpty(adminName)) {
      return { ok: false, message: "Captura el nombre del administrador." };
    }
    const licenseCode = formatted.trim().toUpperCase();
    try {
      const shared = await getSharedPrinterFingerprint();
      const machineName = String(shared?.machineName || window.location.hostname || "").trim();
      const fingerprint = String(shared?.fingerprint || "").trim();
      if (!fingerprint || !machineName) {
        return { ok: false, message: "No se pudo leer PrinterTools. Ábrelo y reintenta." };
      }
      const existing = listStoredTenantProfiles().find((p) =>
        [p.licenseCode, p.tenantKey].map((v) => String(v ?? "").toUpperCase()).includes(licenseCode),
      );
      const alias = String(deviceAlias || existing?.deviceAlias || machineName).trim();
      const devicePublicId = String(existing?.devicePublicId ?? createSecureId()).toUpperCase();
      const deviceKey = String(existing?.deviceKey ?? createDeviceSecret()).toUpperCase();
      const body = {
        licenseCode,
        devicePublicId,
        deviceKey,
        fingerprint,
        fingerprintVersion: isMobileTenant ? "MOBILE_BROWSER_V1" : "PRINTERTOOLS_V1",
        plataforma: isMobileTenant ? (navigator.userAgent || "MOBILE_BROWSER") : (navigator.platform || navigator.userAgent || "WEB_WORKSTATION"),
        nombreDispositivo: alias,
        telefonoDispositivo: isMobileTenant ? alias : "",
        correo: String(adminEmail).trim(),
        nombreUsuario: String(adminName).trim(),
        appKey: "DASHBOARD",
        appVersion: process.env.REACT_APP_VERSION || "1.0.0",
      };
      const responseApi = await fetchTenantWithTimeout(apiTenantMobileDeviceAccessRequest, {
        method: "POST",
        body: JSON.stringify(body),
        headers: { "Content-Type": "application/json", Accept: "application/json" },
      }, 20000);
      const { payload } = await readTenantDebugResponse(responseApi);
      if (!responseApi.ok || payload?.hasError) {
        return { ok: false, message: payload?.message ?? `No fue posible solicitar acceso (${responseApi.status}).` };
      }
      const result = payload?.result ?? payload;
      const statusCode = String(result?.statusCode ?? "PENDING_APPROVAL").toUpperCase();
      const next = persistTenantProfile({
        tenantKey: String(result?.tenantKey ?? licenseCode).toUpperCase(),
        licenseCode,
        devicePublicId,
        deviceKey,
        tenantName: String(result?.tenantName ?? existing?.tenantName ?? "").trim(),
        databaseName: String(result?.databaseName ?? existing?.databaseName ?? "").trim(),
        deviceAlias: alias,
        fingerprint,
        machineName,
        adminEmail: String(adminEmail).trim(),
        adminName: String(adminName).trim(),
        requestId: result?.requestId,
        status: statusCode,
        pendingAccess: statusCode === "PENDING_APPROVAL",
        lastUsedAt: new Date().toISOString(),
      });
      setTenantProfiles(next);
      setTenantCorrelationId(result?.correlationId ?? "");
      return { ok: true, statusCode, message: result?.message ?? "Solicitud enviada. Espera aprobación del propietario.", result };
    } catch (error) {
      return { ok: false, message: `No fue posible enviar la solicitud: ${error?.message ?? "sin conexión"}` };
    }
  };

  return (
    <SesionContext.Provider
      value={{
        usuario, usuarioData,
        tokenRefresh,
        definicionMenu,
        permisosModulos,
        listPermisosObjects,
        usuarioEnSesion,
        configuracion,
        loginForm,
        tenantActivationForm,
        tenantGateStatus,
        tenantGateMessage,
        tenantCorrelationId,
        tenantAdminStatus,
        tenantAdminDevice,
        previousRoute,
        handleChangeLoginForm,
        handleSubmitLoginForm,
        handleTenantActivationFormChange,
        handleSubmitTenantActivation,
        handleRequestMobileDeviceAccess,
        handleRequestWorkstationAccess,
        handleConfirmTenantMfa,
        handleRedeemTenantActivation,
        handleRecoverMobileTenant,
        tenantMfaSetup,
        isRedeemingTenant,
        isRecoveringMobileTenant,
        isMobileTenant,
        mobileVerificationChallenge,
        handleRetryTenantGate,
        handleReassignTenantDevice,
        handleSubmitTenantAdminLogin,
        getTenantAdminLoginDefaults,
        tenantDebugLog: tenantDebugLogEnabled ? tenantDebugLog : "",
        clearTenantDebugLog,
        copyTenantDebugLog,
        handleResetTenantActivation,
        handleDownloadPrinterTools,
        handleClickCerrarSesion,
        getPermisoBoton,
        getNombreBoton,
        getPermisoPagina,
        getNombrePagina,
        getPermisoModulo,
        getNombreModulo,
        handleClickRegresarLogin,
        handleClickAcercaDe,
        handleClicValidarActivarSesion,
        refreshSession,
        handleClickCambiarPassword,
        handleCloseCambiarPassworForm,
        cambiarPasswordForm,
        modalCambioPassword,
        handleChangeCambiarPasswordForm,
        handleSubmitCambiarPasword,
        getDefinicionMenu, setDefinicionMenu,
        pcData,
        getPermisoObject, //Manager de permisos del sistema
        getVisualizacionBoton,
        handleObtenerParametrosCaja, handleObtenerParametrosCajaSinValidacion,
        parametrosCajaForm, setParametrosCajaForm,
        tipoUsuario,
        handleRecargarMenuSesion,
        tenantProfiles,
        refreshTenantProfiles,
        fetchTenantProfilesFromBackend,
        syncActiveDeviceToProfiles,
        switchTenantProfile,
        requestAdditionalTenantAccess,
        getSharedPrinterFingerprint,
      }}
    >
      {props.children}
    </SesionContext.Provider>
  );
};

export const useSesion = () => useContext(SesionContext);
